/* ==========================================================================\n   Theme Commerce Bundle\n   Consolidated global commerce scripts loaded from layout/theme.liquid.\n   Includes swatches, recently viewed, recommendations, quick add, and cart.\n   ========================================================================== */



/* --- Migrated from assets/theme-swatch-utils.js --- */

(() => {
  'use strict';

  const normalizeMultiline = (rawValue) => String(rawValue || '')
    .replace(/&lt;br\s*\/?&gt;/gi, '||')
    .replace(/<br\s*\/?>/gi, '||')
    .replace(/\r?\n/g, '||');

  const splitNormalizedList = (rawValue) => normalizeMultiline(rawValue)
    .split(/\|\||,/)
    .map((value) => value.trim())
    .filter(Boolean);

  const normalizeOptionName = (value) => String(value || '').trim().toLowerCase();

  const parseTriggers = (rawValue) => {
    const unique = new Set();
    splitNormalizedList(rawValue).forEach((value) => {
      const normalized = normalizeOptionName(value);
      if (normalized) unique.add(normalized);
    });
    return Array.from(unique);
  };

  const parseCustomSwatches = (rawValue) => {
    const map = new Map();
    normalizeMultiline(rawValue)
      .split('||')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex < 1) return;

        const key = normalizeOptionName(line.slice(0, separatorIndex));
        const value = line.slice(separatorIndex + 1).trim();
        if (!key || !value) return;

        map.set(key, value);
      });

    return map;
  };

  const isColorOption = (optionName, triggers = []) => {
    const normalized = normalizeOptionName(optionName);
    if (!normalized) return false;
    if (Array.isArray(triggers) && triggers.includes(normalized)) return true;
    return normalized.includes('color') || normalized.includes('colour');
  };

  window.ThemeSwatches = Object.freeze({
    normalizeMultiline,
    splitNormalizedList,
    normalizeOptionName,
    parseTriggers,
    parseCustomSwatches,
    isColorOption
  });
})();



/* --- Migrated from assets/component-card-swatches.js --- */

(() => {
  'use strict';

  if (window.__themeCardSwatchesInitialized) {
    return;
  }
  window.__themeCardSwatchesInitialized = true;

  const SWATCH_SELECTOR = '.card__swatch[data-card-swatch]';

  function isPrimaryPointerActivation(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function setActiveSwatch(card, activeSwatch) {
    const swatches = card.querySelectorAll(SWATCH_SELECTOR);
    swatches.forEach((swatch) => {
      const isActive = swatch === activeSwatch;
      swatch.classList.toggle('is-active', isActive);
      swatch.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function updateCardLinks(card, variantUrl) {
    if (!variantUrl) return;

    const mediaLink = card.querySelector('.card__media-link');
    const titleLink = card.querySelector('.card__title-link');

    mediaLink?.setAttribute('href', variantUrl);
    titleLink?.setAttribute('href', variantUrl);
  }

  function updatePrimaryImage(card, swatch) {
    const primaryImage = card.querySelector('.card__img--primary');
    if (!primaryImage) return;

    const nextSrc = swatch.dataset.variantImageSrc || '';
    const nextSrcset = swatch.dataset.variantImageSrcset || '';
    const nextAlt = swatch.dataset.variantImageAlt || '';

    if (!nextSrc && !nextSrcset) return;

    if (nextSrcset) {
      primaryImage.setAttribute('srcset', nextSrcset);
      const pictureSource = primaryImage.closest('picture')?.querySelector('source[media]');
      pictureSource?.setAttribute('srcset', nextSrcset);
    }

    if (nextSrc) {
      primaryImage.setAttribute('src', nextSrc);
    }

    if (nextAlt) {
      primaryImage.setAttribute('alt', nextAlt);
    }
  }

  function activateSwatch(swatch) {
    const card = swatch.closest('.card--product');
    if (!card) return;

    card.classList.add('card--swatch-selected');
    setActiveSwatch(card, swatch);
    updateCardLinks(card, swatch.dataset.variantUrl || '');
    updatePrimaryImage(card, swatch);

    if (window.ThemeCardRevealScale?.refreshCard) {
      window.ThemeCardRevealScale.refreshCard(card);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;
      if (!isPrimaryPointerActivation(event)) return;

      event.preventDefault();
      activateSwatch(swatch);
    },
    { passive: false }
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ') return;

    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;

    event.preventDefault();
    activateSwatch(swatch);
  });
})();



/* --- Card reveal media scale (dynamic per card) --- */

(() => {
  'use strict';

  if (window.__themeCardRevealScaleInitialized) return;
  window.__themeCardRevealScaleInitialized = true;

  const CARD_SELECTOR = '.card--product.card--reveal-info';
  const SCALE_VAR = '--card-reveal-media-scale-active';
  const DEFAULT_ACTIVE_SCALE = 0.89;
  const MIN_ACTIVE_SCALE = 0.2;
  const MAX_ACTIVE_SCALE = 1;
  const DYNAMIC_SCALE_MQ = '(hover: hover) and (pointer: fine) and (min-width: 1000px)';

  const trackedCards = new Set();
  const queuedCards = new Set();
  let rafId = 0;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const roundDown2 = (value) => Math.floor(value * 100) / 100;
  const canUseDynamicScale = () => window.matchMedia(DYNAMIC_SCALE_MQ).matches;

  function getCardFromTarget(target) {
    if (!(target instanceof Element)) return null;
    return target.closest(CARD_SELECTOR);
  }

  function calculateScale(card) {
    const media = card.querySelector('.card__media');
    const revealGroup = card.querySelector('.card__reveal-group');
    if (!(media instanceof HTMLElement) || !(revealGroup instanceof HTMLElement)) return null;

    // Use layout height (not transformed visual height) so hover scale is not
    // recursively reapplied when swatch clicks trigger a recalculation.
    const mediaHeight = media.offsetHeight || media.getBoundingClientRect().height;
    const revealHeight = revealGroup.getBoundingClientRect().height;
    if (!mediaHeight || !revealHeight) return null;

    const availableMediaHeight = mediaHeight - revealHeight;
    const rawScale = availableMediaHeight / mediaHeight;
    return roundDown2(clamp(rawScale, MIN_ACTIVE_SCALE, MAX_ACTIVE_SCALE));
  }

  function applyScale(card) {
    if (!(card instanceof HTMLElement)) return;
    if (!card.isConnected) {
      trackedCards.delete(card);
      queuedCards.delete(card);
      return;
    }

    // Mobile/tablet and coarse-pointer contexts use the unified static card
    // layout; dynamic reveal scaling is desktop-only.
    if (!canUseDynamicScale()) {
      card.style.removeProperty(SCALE_VAR);
      return;
    }

    const nextScale = calculateScale(card);
    if (nextScale == null) {
      card.style.removeProperty(SCALE_VAR);
      return;
    }

    card.style.setProperty(SCALE_VAR, String(nextScale || DEFAULT_ACTIVE_SCALE));
  }

  function flushQueue() {
    rafId = 0;
    queuedCards.forEach((card) => applyScale(card));
    queuedCards.clear();
  }

  function scheduleCard(card) {
    if (!(card instanceof HTMLElement)) return;
    queuedCards.add(card);
    if (rafId) return;
    rafId = window.requestAnimationFrame(flushQueue);
  }

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const card = getCardFromTarget(entry.target);
        if (card) scheduleCard(card);
      });
    })
    : null;

  function observeCard(card) {
    if (!(card instanceof HTMLElement)) return;
    if (trackedCards.has(card)) return;

    trackedCards.add(card);

    if (resizeObserver) {
      const media = card.querySelector('.card__media');
      const revealGroup = card.querySelector('.card__reveal-group');
      if (media instanceof Element) resizeObserver.observe(media);
      if (revealGroup instanceof Element) resizeObserver.observe(revealGroup);
    }

    scheduleCard(card);
  }

  function refreshAll(root = document) {
    if (!(root instanceof Element || root instanceof Document)) return;

    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      observeCard(card);
      scheduleCard(card);
    });

    trackedCards.forEach((card) => scheduleCard(card));
  }

  function refreshCard(card) {
    const targetCard = card instanceof Element ? card.closest(CARD_SELECTOR) || card : null;
    if (!(targetCard instanceof HTMLElement)) return;
    observeCard(targetCard);
    scheduleCard(targetCard);
  }

  window.ThemeCardRevealScale = Object.freeze({
    refreshAll,
    refreshCard
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshAll(document), { once: true });
  } else {
    refreshAll(document);
  }

  window.addEventListener('load', () => refreshAll(document), { once: true });
  window.addEventListener('resize', () => {
    trackedCards.forEach((card) => scheduleCard(card));
  }, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event?.target) return;
    refreshAll(event.target);
  });

  document.addEventListener('theme:card:reveal-scale:refresh', (event) => {
    const card = event?.detail?.card;
    if (card) {
      refreshCard(card);
      return;
    }
    refreshAll(document);
  });
})();



/* --- Migrated from assets/component-recently-viewed-products.js --- */

/**
 * component-recently-viewed-products.js
 * ---------------------------------------------------------------------------
 * Recently viewed products:
 * 1) Track product views on product pages (localStorage)
 * 2) Hydrate <recently-viewed-products> sections by re-fetching section HTML
 *    via /search with an ID query (Impact-style architecture).
 */

const STORAGE_KEY = 'theme:recently-viewed-products-v2';
const LEGACY_STORAGE_KEY = 'theme:recently-viewed-products';
const COMPAT_STORAGE_KEY = 'viewedProducts';
const MAX_STORED_ITEMS = 50;

function toInt(value, fallback = 0) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getLifetimeDays(value, fallback = 30) {
  const days = toInt(value, fallback);
  return Math.max(1, days);
}

function readStorage(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch (_) {
    return [];
  }
}

function writeStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    // Keep legacy key in sync for backward compatibility with older code paths.
    localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(items.map((item) => item.id)));
  } catch (_) {
    // Safari private mode and storage-restricted environments may throw.
  }
}

function normalizeItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];

  const now = Date.now();
  const normalized = [];

  for (const item of rawItems) {
    let id = 0;
    let viewedAt = now;

    if (typeof item === 'number' || typeof item === 'string') {
      id = toInt(item, 0);
    } else if (item && typeof item === 'object') {
      id = toInt(item.id, 0);
      viewedAt = toInt(item.viewedAt || item.viewed_at || item.ts, now);
    }

    if (id > 0) {
      normalized.push({ id, viewedAt });
    }
  }

  const seen = new Set();
  const deduped = [];
  for (const item of normalized) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }

  return deduped.slice(0, MAX_STORED_ITEMS);
}

function readRecentlyViewedItems() {
  const merged = [
    ...normalizeItems(readStorage(STORAGE_KEY)),
    ...normalizeItems(readStorage(LEGACY_STORAGE_KEY)),
    ...normalizeItems(readStorage(COMPAT_STORAGE_KEY)),
  ];

  return normalizeItems(merged);
}

function pruneExpiredItems(items, lifetimeDays) {
  const cutoff = Date.now() - getLifetimeDays(lifetimeDays) * 24 * 60 * 60 * 1000;
  return items.filter((item) => item.viewedAt >= cutoff);
}

function recordRecentlyViewedProduct(productId, lifetimeDays) {
  if (!productId) return;

  const id = toInt(productId, 0);
  if (id <= 0) return;

  const items = pruneExpiredItems(readRecentlyViewedItems(), lifetimeDays).filter((item) => item.id !== id);
  items.unshift({ id, viewedAt: Date.now() });

  writeStorage(items.slice(0, MAX_STORED_ITEMS));
}

function buildSearchQuery({ count, lifetimeDays, excludeId }) {
  const items = pruneExpiredItems(readRecentlyViewedItems(), lifetimeDays);
  const exclude = toInt(excludeId, 0);
  const filtered = exclude > 0 ? items.filter((item) => item.id !== exclude) : items;

  writeStorage(filtered.slice(0, MAX_STORED_ITEMS));

  const limited = filtered.slice(0, Math.max(1, toInt(count, 6)));
  return limited.map((item) => `id:${item.id}`).join(' OR ');
}

function refreshRevealScope(scope) {
  if (!(scope instanceof Element)) return;
  document.dispatchEvent(
    new CustomEvent('theme:reveal:refresh', {
      detail: { scope }
    })
  );
}

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.isLoaded = false;
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.loadProducts(), { timeout: 1500 });
    } else {
      this.loadProducts();
    }
  }

  async loadProducts() {
    if (this.isLoaded) return;
    this.isLoaded = true;

    const count = this.dataset.productsCount || '6';
    const lifetimeDays = this.dataset.lifetimeDays || document.body.dataset.recentlyViewedLifetime || '30';
    const excludeId = this.dataset.excludeId || '';
    const hideIfEmpty = this.dataset.hideIfEmpty !== 'false';

    const query = buildSearchQuery({ count, lifetimeDays, excludeId });
    const section = this.closest('.shopify-section');

    if (!query) {
      if (hideIfEmpty && section) section.remove();
      return;
    }

    if (!section || !section.id) return;
    const sectionId = section.id.replace('shopify-section-', '');
    const url = `${Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url, { priority: 'low' });
      if (!response.ok) throw new Error(`Failed recently viewed fetch: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedElement = doc.querySelector('recently-viewed-products');
      const hasRenderedCards = Boolean(fetchedElement?.querySelector('.grid-slider__item'));

      if (fetchedElement && hasRenderedCards) {
        this.replaceChildren(...document.importNode(fetchedElement, true).childNodes);
        refreshRevealScope(this);
      } else if (hideIfEmpty && section) {
        section.remove();
      } else if (fetchedElement) {
        this.replaceChildren(...document.importNode(fetchedElement, true).childNodes);
        refreshRevealScope(this);
      }
    } catch (_) {
      if (hideIfEmpty && section) section.remove();
    }
  }
}

if (!window.customElements.get('recently-viewed-products')) {
  window.customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

document.addEventListener('DOMContentLoaded', () => {
  const productId =
    document.body.dataset.productId ||
    document.querySelector('form[data-product-id]')?.dataset.productId ||
    window.meta?.product?.id ||
    '';

  if (!productId) return;
  const lifetimeDays = document.body.dataset.recentlyViewedLifetime || '30';
  recordRecentlyViewedProduct(productId, lifetimeDays);
});



/* --- Migrated from assets/component-product-recommendations.js --- */

/**
 * component-product-recommendations.js
 * ---------------------------------------------------------------------------
 * Hydrates <product-recommendations-list> by fetching Shopify recommendation
 * section HTML and swapping in the loaded markup.
 */

class ProductRecommendationsList extends HTMLElement {
  constructor() {
    super();
    this.isLoaded = false;
    this.intersectionObserver = null;
  }

  connectedCallback() {
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (this.isLoaded) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 350px 0px' }
    );

    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  async loadRecommendations() {
    if (this.isLoaded) return;
    this.isLoaded = true;

    const section = this.closest('.shopify-section');
    const sectionId = this.dataset.sectionId || section?.id?.replace('shopify-section-', '') || '';
    const productId = this.dataset.productId || '';
    const baseUrl = this.dataset.url || '';
    const limit = this.dataset.limit || '6';
    const intent = this.dataset.intent || 'related';
    const hideIfEmpty = this.dataset.hideIfEmpty !== 'false';

    if (!section || !sectionId || !baseUrl || !productId) {
      if (hideIfEmpty && section) section.remove();
      return;
    }

    const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

    try {
      const response = await fetch(url, { priority: 'low' });
      if (!response.ok) throw new Error(`Recommendations fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedElement = doc.querySelector(`product-recommendations-list[data-section-id="${sectionId}"]`) || doc.querySelector('product-recommendations-list');

      if (!fetchedElement) {
        if (hideIfEmpty) section.remove();
        return;
      }

      const hasRenderedCards = Boolean(fetchedElement.querySelector('.grid-slider__item'));

      if (fetchedElement.dataset.hasProducts === 'true' && hasRenderedCards) {
        this.dataset.recommendationsPerformed = 'true';
        this.dataset.hasProducts = 'true';
        this.replaceChildren(...document.importNode(fetchedElement, true).childNodes);
        refreshRevealScope(this);
      } else if (hideIfEmpty) {
        section.remove();
      } else {
        this.dataset.recommendationsPerformed = 'true';
        this.dataset.hasProducts = hasRenderedCards ? 'true' : 'false';
        this.replaceChildren(...document.importNode(fetchedElement, true).childNodes);
        refreshRevealScope(this);
      }
    } catch (_) {
      if (hideIfEmpty) section.remove();
    }
  }
}

if (!window.customElements.get('product-recommendations-list')) {
  window.customElements.define('product-recommendations-list', ProductRecommendationsList);
}



/* --- Migrated from assets/component-quick-add.js --- */

(() => {
  'use strict';

  const sharedSwatchUtils = window.ThemeSwatches || null;

  const TRIGGER_SELECTOR = '[data-quick-add-trigger]';
  const CLOSE_SELECTOR = '[data-quick-add-close]';
  const OPTION_SELECTOR = '[data-qa-option-index][data-qa-option-value]';
  const QTY_INPUT_SELECTOR = '[data-qa-qty-input]';
  const QTY_DECREASE_SELECTOR = '[data-qa-qty-decrease]';
  const QTY_INCREASE_SELECTOR = '[data-qa-qty-increase]';
  const FORM_SELECTOR = '[data-quick-add-form]';
  const QUICK_ADD_FRAGMENT_SELECTOR = '[data-quick-add-fragment]';
  const QUICK_ADD_PRODUCT_JSON_SELECTOR = '[data-quick-add-product-json]';
  const QUICK_ADD_MEDIA_SWIPER_SELECTOR = '[data-qa-media-swiper]';

  const LOCAL_LOCK_ATTR = 'data-theme-quick-add-lock-count';
  const LOCAL_LOCK_CLASS = 'theme-quick-add-lock';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const DEFAULT_DURATION = 320;
  let swiperModulePromise = null;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const parseDurationMs = (rawValue) => {
    if (!rawValue) return NaN;
    const value = rawValue.trim();
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return Number.parseFloat(value);
  };

  const getScrollbarCompensation = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const loadSwiper = async (assetUrl) => {
    if (!assetUrl) return null;
    if (!swiperModulePromise) {
      swiperModulePromise = import(assetUrl)
        .then((module) => module?.default || module?.Swiper || null)
        .catch(() => null);
    }
    return swiperModulePromise;
  };

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  class QuickAddDrawer extends HTMLElement {
    constructor() {
      super();

      this.panel = null;
      this.content = null;
      this.statusRegion = null;
      this.headerTitle = null;

      this.isOpen = false;
      this.closeTimer = 0;
      this.fetchController = null;

      this.productData = null;
      this.currentVariant = null;
      this.selectedOptions = [];
      this.currentProductHandle = '';
      this.currentProductUrl = '';
      this.lastTrigger = null;
      this.isLoadingProduct = false;
      this.loadToken = 0;
      this.swatchTriggers = [];
      this.customSwatches = new Map();
      this.optionSwatches = new Map();
      this.swiperAssetUrl = '';
      this.mediaSwiper = null;
      this.mediaSwiperRenderToken = 0;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onContentClick = this.onContentClick.bind(this);
      this.onContentChange = this.onContentChange.bind(this);
      this.onContentSubmit = this.onContentSubmit.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-quick-add-panel]');
      this.content = this.querySelector('[data-quick-add-content]');
      this.statusRegion = this.querySelector('[data-quick-add-status]');
      this.errorRegion = this.querySelector('[data-quick-add-error]');
      this.headerTitle = this.querySelector('.quick-add-drawer__title');
      this.swiperAssetUrl = this.dataset.swiperUrl || '';

      if (!this.panel || !this.content) return;

      this.swatchTriggers = this.parseSwatchTriggers(this.dataset.swatchTrigger || '');
      this.customSwatches = this.parseCustomSwatches(this.dataset.customSwatches || '');

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.content.addEventListener('click', this.onContentClick);
      this.content.addEventListener('change', this.onContentChange);
      this.content.addEventListener('submit', this.onContentSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.content?.removeEventListener('click', this.onContentClick);
      this.content?.removeEventListener('change', this.onContentChange);
      this.content?.removeEventListener('submit', this.onContentSubmit);
      window.clearTimeout(this.closeTimer);
      this.abortRequest();
      this.destroyMediaSwiper();

      if (this.isOpen) {
        this.isOpen = false;
        this.unlockScroll();
      }
    }

    get labels() {
      return {
        addToCart: this.dataset.labelAddToCart || 'Add to cart',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        chooseOptions: this.dataset.labelChooseOptions || 'Choose options',
        viewDetails: this.dataset.labelViewDetails || 'Détails du produit',
        decrease: this.dataset.labelDecrease || 'Decrease quantity',
        increase: this.dataset.labelIncrease || 'Increase quantity',
        unavailable: this.dataset.labelUnavailable || 'Sold out or unavailable',
        loading: this.dataset.labelLoading || 'Loading...',
        error: this.dataset.labelError || 'Something went wrong. Please try again.',
        sellingPlanRequired:
          this.dataset.labelSellingPlanRequired ||
          'This product can only be purchased with a subscription.'
      };
    }

    get routesRoot() {
      return this.dataset.routesRoot || '/';
    }

    get cartType() {
      return this.dataset.cartType || document.body.dataset.cartType || 'drawer';
    }

    get cartUrl() {
      return this.dataset.cartUrl || '/cart';
    }

    get moneyFormat() {
      return this.dataset.moneyFormat || window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount}}';
    }

    parseSwatchTriggers(rawValue) {
      if (sharedSwatchUtils?.parseTriggers) {
        return sharedSwatchUtils.parseTriggers(rawValue);
      }

      const normalized = String(rawValue || '')
        .replace(/&lt;br\s*\/?&gt;/gi, '||')
        .replace(/<br\s*\/?>/gi, '||')
        .replace(/\r?\n/g, '||');

      return normalized
        .split(/\|\||,/)
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    parseCustomSwatches(rawValue) {
      if (sharedSwatchUtils?.parseCustomSwatches) {
        return sharedSwatchUtils.parseCustomSwatches(rawValue);
      }

      const map = new Map();
      const normalized = String(rawValue || '')
        .replace(/&lt;br\s*\/?&gt;/gi, '||')
        .replace(/<br\s*\/?>/gi, '||')
        .replace(/\r?\n/g, '||');
      const lines = normalized
        .split('||')
        .map((line) => line.trim())
        .filter(Boolean);

      lines.forEach((line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex < 1) return;

        const key = line.slice(0, separatorIndex).trim().toLowerCase();
        const value = line.slice(separatorIndex + 1).trim();
        if (!key || !value) return;
        map.set(key, value);
      });

      return map;
    }

    parseOptionSwatches(rawValue) {
      const map = new Map();
      if (!rawValue) return map;

      try {
        const payload = JSON.parse(rawValue);
        if (!payload || typeof payload !== 'object') return map;

        Object.entries(payload).forEach(([key, value]) => {
          if (!key || !value) return;
          map.set(String(key).toLowerCase().trim(), String(value));
        });
      } catch (_error) {
        return map;
      }

      return map;
    }

    extractSwatchMapFromCard(trigger) {
      const map = new Map();
      const card = trigger?.closest('.card--product');
      if (card) {
        const swatches = card.querySelectorAll('.card__swatch[data-card-swatch]');
        swatches.forEach((swatch) => {
          const label = String(
            swatch.getAttribute('title')
              || swatch.getAttribute('aria-label')
              || swatch.querySelector('.visually-hidden')?.textContent
              || ''
          ).trim().toLowerCase();

          if (!label) return;

          const background = String(swatch.style.getPropertyValue('--swatch-bg') || '').trim();
          if (!background) return;

          map.set(label, background);
        });
      }

      const recommendationCard = trigger?.closest('.theme-cart-featured__item');
      if (recommendationCard) {
        const recommendationSwatches = recommendationCard.querySelectorAll('.theme-cart-featured__swatch[title]');
        recommendationSwatches.forEach((swatch) => {
          const label = String(swatch.getAttribute('title') || '').trim().toLowerCase();
          if (!label) return;

          const background = String(
            swatch.style.getPropertyValue('--swatch-bg')
              || swatch.style.getPropertyValue('--swatch-background')
              || ''
          ).trim();
          if (!background) return;

          map.set(label, background);
        });
      }

      return map;
    }

    isSwatchOption(optionName) {
      const normalized = String(optionName || '').trim().toLowerCase();
      if (!normalized) return false;
      if (sharedSwatchUtils?.isColorOption) {
        return sharedSwatchUtils.isColorOption(normalized, this.swatchTriggers);
      }
      if (this.swatchTriggers.includes(normalized)) return true;
      return normalized.includes('color') || normalized.includes('colour');
    }

    resolveSwatchBackground(optionValue) {
      return this.resolveSwatchBackgroundByOption('', optionValue);
    }

    getOptionIndexByName(optionName) {
      const normalizedName = String(optionName || '').trim().toLowerCase();
      if (!normalizedName || !Array.isArray(this.productData?.options)) return -1;

      return this.productData.options.findIndex((optionRaw) => {
        const rawName = typeof optionRaw === 'string' ? optionRaw : optionRaw?.name;
        return String(rawName || '').trim().toLowerCase() === normalizedName;
      });
    }

    resolveVariantImageSwatchByOption(optionName, optionValue) {
      const optionIndex = this.getOptionIndexByName(optionName);
      if (optionIndex < 0 || !Array.isArray(this.productData?.variants)) return '';

      const targetValue = String(optionValue || '').trim();
      if (!targetValue) return '';

      const matchingVariant = this.productData.variants.find((variant) => {
        const variantValue = variant?.[this.optionKey(optionIndex)];
        return String(variantValue || '') === targetValue;
      });

      if (!matchingVariant) return '';
      const imageSrc =
        this.extractImageSrc(matchingVariant?.featured_media)
        || this.extractImageSrc(matchingVariant?.featured_image)
        || this.extractImageSrc(matchingVariant?.image);
      if (!imageSrc) return '';

      return `url(${imageSrc})`;
    }

    resolveSwatchBackgroundByOption(optionName, optionValue) {
      const optionKey = String(optionName || '').trim().toLowerCase();
      const valueKey = String(optionValue || '').trim().toLowerCase();
      if (!valueKey) return '';

      if (this.customSwatches.has(valueKey)) {
        return this.customSwatches.get(valueKey);
      }

      if (optionKey) {
        const combinedKey = `${optionKey}::${valueKey}`;
        if (this.optionSwatches.has(combinedKey)) {
          return this.optionSwatches.get(combinedKey);
        }
      }

      if (this.optionSwatches.has(valueKey)) {
        return this.optionSwatches.get(valueKey);
      }

      const variantImageSwatch = this.resolveVariantImageSwatchByOption(optionName, optionValue);
      if (variantImageSwatch) {
        return variantImageSwatch;
      }

      return 'rgb(var(--color-foreground-rgb) / 0.14)';
    }

    getMotionDuration() {
      const duration = parseDurationMs(
        window.getComputedStyle(this.panel || document.documentElement).getPropertyValue('--motion-duration-normal')
      );
      return Number.isFinite(duration) && duration > 0 ? duration : DEFAULT_DURATION;
    }

    lockScroll() {
      const root = document.documentElement;
      const body = document.body;
      const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

      if (current <= 0) {
        root.classList.add(LOCAL_LOCK_CLASS);
        body.classList.add(LOCAL_LOCK_CLASS);
        acquireGlobalScrollLock();
      }

      root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
    }

    unlockScroll() {
      const root = document.documentElement;
      const body = document.body;
      const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);
      const next = Math.max(0, current - 1);

      if (next === 0) {
        root.classList.remove(LOCAL_LOCK_CLASS);
        body.classList.remove(LOCAL_LOCK_CLASS);
        root.removeAttribute(LOCAL_LOCK_ATTR);
        releaseGlobalScrollLock();
        return;
      }

      root.setAttribute(LOCAL_LOCK_ATTR, String(next));
    }

    onDocumentClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const closeTarget = target.closest(CLOSE_SELECTOR);
      if (closeTarget && this.contains(closeTarget)) {
        event.preventDefault();
        this.close(true);
        return;
      }

      const trigger = target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;

      if (event.defaultPrevented) return;
      if (!this.isPrimaryActivation(event)) return;
      if (trigger.getAttribute('aria-busy') === 'true') return;
      if (trigger.getAttribute('aria-disabled') === 'true') return;
      if (trigger.hasAttribute('disabled')) return;

      // If we cannot resolve a valid product handle, keep native link behavior.
      if (!this.getHandleFromTrigger(trigger)) return;

      event.preventDefault();
      this.openFromTrigger(trigger);
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;
      this.trapFocus(event);
    }

    onContentClick(event) {
      const optionButton = event.target.closest(OPTION_SELECTOR);
      if (optionButton) {
        event.preventDefault();
        const optionIndex = Number.parseInt(optionButton.dataset.qaOptionIndex, 10);
        const optionValue = optionButton.dataset.qaOptionValue || '';
        if (Number.isFinite(optionIndex) && optionValue) {
          this.selectOption(optionIndex, optionValue);
        }
        return;
      }

      const qtyInput = this.content.querySelector(QTY_INPUT_SELECTOR);
      if (!qtyInput) return;

      if (event.target.closest(QTY_DECREASE_SELECTOR)) {
        event.preventDefault();
        this.setQuantity(qtyInput, (Number.parseInt(qtyInput.value, 10) || 1) - 1);
      } else if (event.target.closest(QTY_INCREASE_SELECTOR)) {
        event.preventDefault();
        this.setQuantity(qtyInput, (Number.parseInt(qtyInput.value, 10) || 1) + 1);
      }
    }

    onContentSubmit(event) {
      const form = event.target.closest(FORM_SELECTOR);
      if (!form) return;
      event.preventDefault();
      this.submitQuickAdd(form);
    }

    onContentChange(event) {
      const optionInput = event.target.closest(OPTION_SELECTOR);
      if (!optionInput) return;

      const optionIndex = Number.parseInt(optionInput.dataset.qaOptionIndex, 10);
      const optionValue = optionInput.dataset.qaOptionValue || '';
      if (Number.isFinite(optionIndex) && optionValue) {
        this.selectOption(optionIndex, optionValue);
      }
    }

    isPrimaryActivation(event) {
      if (event.type !== 'click') return true;
      return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    trapFocus(event) {
      const focusables = Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusables.length) {
        event.preventDefault();
        this.focusElement(this.panel);
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!this.panel.contains(active)) {
        event.preventDefault();
        this.focusElement(first);
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    abortRequest() {
      if (!this.fetchController) return;
      this.fetchController.abort();
      this.fetchController = null;
    }

    clearTriggerState() {
      if (!this.lastTrigger) return;
      this.lastTrigger.setAttribute('aria-expanded', 'false');
      this.setTriggerLoading(this.lastTrigger, false);
    }

    setTriggerLoading(trigger, isLoading) {
      if (!trigger) return;

      if (isLoading) {
        trigger.setAttribute('aria-busy', 'true');
        trigger.setAttribute('data-quick-add-loading', 'true');
        if (trigger.tagName === 'A') {
          const prevTabindex = trigger.getAttribute('tabindex');
          trigger.setAttribute('data-quick-add-prev-tabindex', prevTabindex === null ? '__none__' : prevTabindex);
          trigger.setAttribute('tabindex', '-1');
          trigger.setAttribute('aria-disabled', 'true');
        }
      } else {
        trigger.removeAttribute('aria-busy');
        trigger.removeAttribute('data-quick-add-loading');
        if (trigger.tagName === 'A') {
          const prevTabindex = trigger.getAttribute('data-quick-add-prev-tabindex');
          if (prevTabindex && prevTabindex !== '__none__') {
            trigger.setAttribute('tabindex', prevTabindex);
          } else {
            trigger.removeAttribute('tabindex');
          }
          trigger.removeAttribute('data-quick-add-prev-tabindex');
          trigger.removeAttribute('aria-disabled');
        }
      }
    }

    sanitizeUrlValue(rawValue) {
      if (!rawValue) return '';
      return String(rawValue).trim().replace(/^["']+/, '').replace(/["']+$/, '').trim();
    }

    appendPreviewParams(urlLike) {
      const sanitizedUrl = this.sanitizeUrlValue(urlLike);
      if (!sanitizedUrl) return '';

      try {
        const url = new URL(sanitizedUrl, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        const previewKeys = ['preview_theme_id', 'preview_pathname', '_fd', 'pb', 'key'];

        previewKeys.forEach((key) => {
          if (!currentParams.has(key) || url.searchParams.has(key)) return;
          url.searchParams.set(key, currentParams.get(key));
        });

        const normalizedPath = url.pathname
          .replace(/^\/%22/, '/')
          .replace(/%22(?=\/|$)/g, '')
          .replace(/\/{2,}/g, '/');

        return `${normalizedPath}${url.search}`;
      } catch (_error) {
        return sanitizedUrl;
      }
    }

    getRawProductUrlFromTrigger(trigger) {
      return this.sanitizeUrlValue(
        trigger?.dataset.productUrl
          || trigger?.dataset.quickAddUrl
          || trigger?.getAttribute('href')
          || ''
      );
    }

    async openFromTrigger(trigger) {
      const productHandle = this.getHandleFromTrigger(trigger);
      if (!productHandle) return;

      const shouldCloseCartDrawer = trigger.hasAttribute('data-quick-add-close-cart')
        || Boolean(trigger.closest('[data-cart-drawer]'));

      if (this.isLoadingProduct && this.lastTrigger === trigger) return;

      this.abortRequest();
      this.isLoadingProduct = true;
      const currentLoadToken = ++this.loadToken;

      this.clearTriggerState();
      this.lastTrigger = trigger;
      this.lastTrigger.setAttribute('aria-expanded', 'true');
      this.setTriggerLoading(this.lastTrigger, true);

      const productTitle = trigger.dataset.productTitle || this.getProductTitleFromTrigger(trigger) || this.labels.chooseOptions;
      this.currentProductHandle = productHandle;
      this.currentProductUrl = this.getProductUrlFromTrigger(trigger, productHandle);
      this.optionSwatches = this.parseOptionSwatches(trigger.dataset.qaSwatchMap || '');
      const cardSwatchMap = this.extractSwatchMapFromCard(trigger);
      cardSwatchMap.forEach((value, key) => {
        this.optionSwatches.set(key, value);
      });
      if (this.headerTitle) {
        this.headerTitle.textContent = productTitle;
      }

      try {
        const payload = await this.loadProductFromHtml(this.currentProductUrl, this.currentProductHandle);
        if (currentLoadToken !== this.loadToken) return;
        if (!payload) {
          throw new Error(this.labels.error);
        }

        this.productData = payload.product;
        if (payload.productUrl) {
          this.currentProductUrl = payload.productUrl;
        }
        if (payload.productTitle && this.headerTitle) {
          this.headerTitle.textContent = payload.productTitle;
        }
        this.initializeVariantState();
        this.renderProduct();

        if (shouldCloseCartDrawer) {
          document.dispatchEvent(new CustomEvent('theme:cart:close'));
        }

        this.open();
      } catch (error) {
        if (currentLoadToken !== this.loadToken) return;
        if (error && error.name === 'AbortError') return;
        const fallbackUrl = this.appendPreviewParams(
          this.currentProductUrl || this.getRawProductUrlFromTrigger(trigger)
        );
        if (fallbackUrl) {
          window.location.assign(fallbackUrl);
          return;
        }
        this.renderError(this.labels.error);
        this.clearTriggerState();
      } finally {
        if (currentLoadToken === this.loadToken) {
          this.isLoadingProduct = false;
        }
        this.setTriggerLoading(trigger, false);
      }
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      window.clearTimeout(this.closeTimer);

      this.classList.remove('is-closing');
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-opening');

      window.requestAnimationFrame(() => {
        this.classList.remove('is-opening');
        this.classList.add('is-open');
        this.lockScroll();

        window.requestAnimationFrame(() => {
          this.panel.focus({ preventScroll: true });
        });
      });
    }

    close(restoreFocus) {
      if (!this.isOpen) return;
      const focusTarget = restoreFocus ? this.resolveFocusRestoreTarget() : null;

      this.isOpen = false;
      this.abortRequest();
      this.destroyMediaSwiper();

      this.classList.remove('is-open');
      this.classList.add('is-closing');
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();

      const duration = this.getMotionDuration();
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.classList.remove('is-closing');
        this.setAttribute('hidden', '');
      }, duration + 40);

      if (focusTarget) {
        window.requestAnimationFrame(() => {
          this.focusElement(focusTarget);
        });
      }

      this.clearTriggerState();
    }

    resolveFocusRestoreTarget() {
      let target = this.lastTrigger instanceof HTMLElement ? this.lastTrigger : null;
      if (!target) return null;

      if (target.hasAttribute('disabled') || target.getAttribute('aria-disabled') === 'true') {
        return null;
      }

      if (target.closest('[data-cart-drawer]')) {
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        const cartIsOpen = Boolean(
          cartDrawer
          && cartDrawer.classList.contains('is-open')
          && !cartDrawer.hasAttribute('hidden')
          && cartDrawer.getAttribute('aria-hidden') !== 'true'
        );

        if (!cartIsOpen) {
          const fallbackTrigger = document.querySelector('[data-cart-trigger][data-cart-behavior="drawer"]');
          if (fallbackTrigger instanceof HTMLElement) {
            target = fallbackTrigger;
          }
        }
      }

      if (!document.contains(target)) return null;

      const isHidden = target.closest('[hidden], [aria-hidden="true"]')
        || (target.getClientRects().length === 0 && window.getComputedStyle(target).position !== 'fixed');
      if (isHidden) return null;

      return target;
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;
      if (typeof element.focus !== 'function') return;

      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }

    getHandleFromTrigger(trigger) {
      if (trigger.dataset.quickAddHandle) {
        return trigger.dataset.quickAddHandle;
      }

      const cardHandle = trigger.closest('[data-product-handle]')?.getAttribute('data-product-handle');
      if (cardHandle) {
        return cardHandle;
      }

      const sourceUrl = this.getRawProductUrlFromTrigger(trigger);
      const match = sourceUrl.match(/\/products\/([^/?#]+)/);
      if (!match) return '';

      try {
        return decodeURIComponent(match[1]);
      } catch (_error) {
        return match[1];
      }
    }

    getProductTitleFromTrigger(trigger) {
      const card = trigger.closest('.card');
      if (!card) return '';
      const titleEl = card.querySelector('.card__title a, .card__title');
      return titleEl?.textContent?.trim() || '';
    }

    getProductUrlFromTrigger(trigger, handle) {
      const sourceUrl = this.getRawProductUrlFromTrigger(trigger);
      if (sourceUrl) {
        try {
          const url = new URL(sourceUrl, window.location.origin);
          return this.appendPreviewParams(`${url.pathname}${url.search || ''}`);
        } catch (_error) {
          return this.appendPreviewParams(sourceUrl);
        }
      }

      const root = (this.routesRoot || '/').replace(/\/?$/, '/');
      return this.appendPreviewParams(`${root}products/${encodeURIComponent(handle)}`);
    }

    renderLoading() {
      this.destroyMediaSwiper();
      this.mediaSwiperRenderToken += 1;
      this.content.innerHTML = `<div class="quick-add-drawer__state quick-add-drawer__state--loading">${this.escapeHtml(this.labels.loading)}</div>`;
      this.setStatusText(this.labels.loading);
    }

    renderError(message) {
      this.destroyMediaSwiper();
      this.mediaSwiperRenderToken += 1;
      const text = message || this.labels.error;
      this.content.innerHTML = `<p class="quick-add-product__message">${this.escapeHtml(text)}</p>`;
      this.setStatusText(text);
    }

    parseQuickAddHtmlPayload(htmlText, fallbackProductUrl = '') {
      const parsedDocument = new DOMParser().parseFromString(htmlText, 'text/html');
      const fragment = parsedDocument.querySelector(QUICK_ADD_FRAGMENT_SELECTOR);
      if (!fragment) return null;

      const productJsonScript = fragment.querySelector(QUICK_ADD_PRODUCT_JSON_SELECTOR);
      if (!productJsonScript) return null;

      let parsedProduct = null;
      try {
        parsedProduct = JSON.parse(productJsonScript.textContent || '{}');
      } catch (_error) {
        return null;
      }

      if (!parsedProduct || typeof parsedProduct !== 'object') return null;

      const fragmentProductUrl = this.appendPreviewParams(fragment.getAttribute('data-product-url') || fallbackProductUrl);
      const fragmentProductTitle = fragment.getAttribute('data-product-title') || parsedProduct.title || '';

      if (fragmentProductUrl) {
        parsedProduct.url = fragmentProductUrl;
      }

      return {
        product: parsedProduct,
        productUrl: fragmentProductUrl,
        productTitle: fragmentProductTitle
      };
    }

    async loadProductFromHtml(productUrl, handle = '') {
      this.abortRequest();
      this.fetchController = new AbortController();

      try {
        const requestOptions = {
          signal: this.fetchController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        };
        const root = (this.routesRoot || '/').replace(/\/?$/, '/');
        const candidates = [];

        if (productUrl) {
          candidates.push(this.appendPreviewParams(productUrl));
        }

        if (handle) {
          candidates.push(this.appendPreviewParams(`${root}products/${encodeURIComponent(handle)}`));
          candidates.push(this.appendPreviewParams(`/products/${encodeURIComponent(handle)}`));
        }

        const uniqueCandidates = [...new Set(candidates.filter(Boolean))];
        let payload = null;
        let response = null;

        for (const candidateUrl of uniqueCandidates) {
          response = await fetch(candidateUrl, requestOptions);
          if (!response.ok) continue;

          const htmlText = await response.text();
          payload = this.parseQuickAddHtmlPayload(htmlText, candidateUrl);
          if (payload?.product) break;
        }

        if (!payload || !payload.product) {
          throw new Error(response ? String(response.status) : 'load_error');
        }

        return payload;
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        throw error;
      } finally {
        this.fetchController = null;
      }
    }

    initializeVariantState() {
      if (!this.productData || !Array.isArray(this.productData.variants)) {
        this.currentVariant = null;
        this.selectedOptions = [];
        return;
      }

      this.currentVariant = this.productData.variants.find((variant) => variant.available) || this.productData.variants[0] || null;
      this.selectedOptions = this.variantToOptionArray(this.currentVariant);
    }

    variantToOptionArray(variant) {
      if (!variant) return [];
      return [variant.option1, variant.option2, variant.option3].slice(0, this.productData.options.length);
    }

    optionKey(index) {
      return `option${index + 1}`;
    }

    matchesSelection(variant, values) {
      for (let index = 0; index < values.length; index += 1) {
        if (values[index] && variant[this.optionKey(index)] !== values[index]) {
          return false;
        }
      }
      return true;
    }

    getOptionValues(optionIndex) {
      const key = this.optionKey(optionIndex);
      const unique = new Set();
      (this.productData.variants || []).forEach((variant) => {
        const value = variant[key];
        if (value) unique.add(value);
      });
      return Array.from(unique);
    }

    getCompatibleVariants(selectionValues, ignoreIndex) {
      return (this.productData.variants || []).filter((variant) => {
        for (let index = 0; index < selectionValues.length; index += 1) {
          if (index === ignoreIndex) continue;
          const selectedValue = selectionValues[index];
          if (!selectedValue) continue;
          if (variant[this.optionKey(index)] !== selectedValue) {
            return false;
          }
        }
        return true;
      });
    }

    findVariantForOptionSelection(optionIndex, optionValue) {
      const nextSelection = [...this.selectedOptions];
      nextSelection[optionIndex] = optionValue;

      const exact = (this.productData.variants || []).find((variant) => this.matchesSelection(variant, nextSelection));
      if (exact) return exact;

      const key = this.optionKey(optionIndex);
      const scopedCandidates = this.getCompatibleVariants(nextSelection, optionIndex)
        .filter((variant) => variant[key] === optionValue);
      const candidates = scopedCandidates.length
        ? scopedCandidates
        : (this.productData.variants || []).filter((variant) => variant[key] === optionValue);
      return candidates.find((variant) => variant.available) || candidates[0] || null;
    }

    getValueState(optionIndex, optionValue, selectionValues = this.selectedOptions) {
      const key = this.optionKey(optionIndex);
      const matching = this.getCompatibleVariants(selectionValues, optionIndex)
        .filter((variant) => variant[key] === optionValue);

      if (!matching.length) return 'unavailable';
      if (matching.some((variant) => variant.available)) return 'available';
      return 'sold-out';
    }

    selectOption(optionIndex, optionValue) {
      const variant = this.findVariantForOptionSelection(optionIndex, optionValue);
      const nextSelection = [...this.selectedOptions];
      nextSelection[optionIndex] = optionValue;

      if (variant) {
        this.currentVariant = variant;
        this.selectedOptions = this.variantToOptionArray(variant);
      } else {
        this.selectedOptions = nextSelection;
        this.currentVariant = null;
      }

      this.updateRenderedProductState();
    }

    formatMoney(cents) {
      if (typeof cents !== 'number') return '';

      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100).toString();
      const amountWithComma = amount.replace('.', ',');
      const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return this.moneyFormat
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace(/\{\{amount\}\}/g, amount);
    }

    getVariantImage() {
      return this.extractImageSrc(this.currentVariant?.featured_image) || '';
    }

    extractImageSrc(imageLike) {
      if (!imageLike) return '';
      if (typeof imageLike === 'string') return imageLike;
      if (typeof imageLike?.src === 'string') return imageLike.src;
      if (typeof imageLike?.url === 'string') return imageLike.url;
      if (typeof imageLike?.featured_image?.src === 'string') return imageLike.featured_image.src;
      if (typeof imageLike?.preview_image?.src === 'string') return imageLike.preview_image.src;
      if (typeof imageLike?.preview_image?.url === 'string') return imageLike.preview_image.url;
      if (typeof imageLike?.image?.src === 'string') return imageLike.image.src;
      return '';
    }

    normalizeImageKey(imageSrc) {
      const source = String(imageSrc || '').trim();
      if (!source) return '';

      try {
        const url = new URL(source, window.location.origin);
        return `${url.pathname}`.toLowerCase();
      } catch (_error) {
        return source
          .replace(/^https?:/i, '')
          .replace(/^\/\//, '')
          .split('?')[0]
          .toLowerCase();
      }
    }

    collectGalleryImages(productTitle) {
      const images = [];
      const seen = new Set();

      const appendImage = (imageLike, altFallback = productTitle) => {
        const src = this.extractImageSrc(imageLike);
        if (!src) return;

        const key = this.normalizeImageKey(src);
        if (!key || seen.has(key)) return;
        seen.add(key);

        const imageAlt = typeof imageLike?.alt === 'string' && imageLike.alt.trim()
          ? imageLike.alt
          : altFallback;

        images.push({
          src,
          alt: imageAlt || productTitle
        });
      };

      appendImage(this.productData?.featured_image, productTitle);

      if (Array.isArray(this.productData?.images)) {
        this.productData.images.forEach((imageLike) => appendImage(imageLike, productTitle));
      }

      if (Array.isArray(this.productData?.media)) {
        this.productData.media.forEach((mediaItem) => {
          if (mediaItem?.media_type && mediaItem.media_type !== 'image') return;
          appendImage(mediaItem, productTitle);
        });
      }

      return images;
    }

    getActiveMediaIndex(images) {
      if (!Array.isArray(images) || !images.length) return 0;

      const variantImageSrc = this.getVariantImage();
      if (!variantImageSrc) return 0;

      const variantKey = this.normalizeImageKey(variantImageSrc);
      if (!variantKey) return 0;

      const index = images.findIndex((image) => this.normalizeImageKey(image.src) === variantKey);
      return index >= 0 ? index : 0;
    }

    renderMediaMarkup(images, activeIndex, productTitle) {
      if (!Array.isArray(images) || !images.length) {
        return '<div class="quick-add-product__image" aria-hidden="true"></div>';
      }

      if (images.length === 1) {
        const image = images[0];
        return `<img class="quick-add-product__image" src="${this.escapeHtml(image.src)}" alt="${this.escapeHtml(image.alt || productTitle)}" loading="lazy">`;
      }

      const slidesMarkup = images
        .map((image, index) => `
          <div class="quick-add-product__media-slide" data-qa-media-slide="${index}" data-qa-image-key="${this.escapeHtml(this.normalizeImageKey(image.src))}">
            <img class="quick-add-product__image" src="${this.escapeHtml(image.src)}" alt="${this.escapeHtml(image.alt || productTitle)}" loading="${index === activeIndex ? 'eager' : 'lazy'}">
          </div>
        `)
        .join('');

      return `
        <div class="quick-add-product__media-swiper" data-qa-media-swiper data-qa-initial-slide="${activeIndex}">
          <div class="quick-add-product__media-track">
            ${slidesMarkup}
          </div>
        </div>
      `;
    }

    destroyMediaSwiper() {
      if (!this.mediaSwiper) return;
      this.mediaSwiper.destroy(true, true);
      this.mediaSwiper = null;
    }

    async initMediaSwiper() {
      const mediaSwiperEl = this.content.querySelector(QUICK_ADD_MEDIA_SWIPER_SELECTOR);
      if (!mediaSwiperEl) return;

      const slideCount = mediaSwiperEl.querySelectorAll('.quick-add-product__media-slide').length;
      if (slideCount < 2) return;

      const renderToken = this.mediaSwiperRenderToken;
      const Swiper = await loadSwiper(this.swiperAssetUrl);
      if (!Swiper || renderToken !== this.mediaSwiperRenderToken) return;

      const initialSlide = Math.max(
        0,
        Number.parseInt(mediaSwiperEl.getAttribute('data-qa-initial-slide') || '0', 10) || 0
      );
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.destroyMediaSwiper();

      this.mediaSwiper = new Swiper(mediaSwiperEl, {
        wrapperClass: 'quick-add-product__media-track',
        slideClass: 'quick-add-product__media-slide',
        slidesPerView: 2.3,
        spaceBetween: 5,
        speed: prefersReducedMotion ? 0 : 640,
        grabCursor: true,
        watchOverflow: true,
        resistanceRatio: 0.78,
        followFinger: true,
        initialSlide,
        a11y: {
          enabled: true
        }
      });
    }

    getCurrentProductUrl() {
      const baseProductUrl =
        this.currentProductUrl
        || this.productData?.url
        || `${this.routesRoot}products/${encodeURIComponent(this.productData?.handle || '')}`;
      if (!this.currentVariant?.id) return baseProductUrl;
      const variantConnector = baseProductUrl.includes('?') ? '&' : '?';
      return `${baseProductUrl}${variantConnector}variant=${this.currentVariant.id}`;
    }

    getVariantMediaSlideIndex() {
      const variantImage = this.getVariantImage();
      if (!variantImage) return -1;

      const variantKey = this.normalizeImageKey(variantImage);
      if (!variantKey) return -1;

      const slides = Array.from(this.content.querySelectorAll('[data-qa-media-slide]'));
      if (!slides.length) return -1;

      return slides.findIndex((slide) => String(slide.getAttribute('data-qa-image-key') || '') === variantKey);
    }

    slideMediaToCurrentVariant() {
      const targetIndex = this.getVariantMediaSlideIndex();
      if (targetIndex < 0) return;

      const mediaSwiperEl = this.content.querySelector(QUICK_ADD_MEDIA_SWIPER_SELECTOR);
      if (mediaSwiperEl) {
        mediaSwiperEl.setAttribute('data-qa-initial-slide', String(targetIndex));
      }

      if (!this.mediaSwiper) return;
      if (this.mediaSwiper.activeIndex === targetIndex) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mediaSwiper.slideTo(targetIndex, prefersReducedMotion ? 0 : 620);
    }

    updateRenderedProductState() {
      const productRoot = this.content.querySelector('[data-quick-add-product]');
      if (!productRoot) {
        this.renderProduct();
        return;
      }

      const productUrl = this.getCurrentProductUrl();
      const canAdd = Boolean(this.currentVariant && this.currentVariant.available);
      const submitLabel = canAdd ? this.labels.addToCart : this.labels.soldOut;

      const mediaLink = productRoot.querySelector('.quick-add-product__media');
      if (mediaLink) {
        mediaLink.setAttribute('href', productUrl);
      }

      const optionsEl = productRoot.querySelector('.quick-add-product__options');
      if (optionsEl) {
        optionsEl.innerHTML = this.renderOptionsMarkup();
      }

      const metaEl = productRoot.querySelector('.quick-add-product__meta');
      if (metaEl) {
        const priceEl = metaEl.querySelector('.card-price');
        const priceMarkup = this.getCurrentPriceMarkup();
        if (priceEl) {
          priceEl.outerHTML = priceMarkup;
        } else {
          metaEl.insertAdjacentHTML('beforeend', priceMarkup);
        }

        const availabilityEl = metaEl.querySelector('.quick-add-product__availability');
        if (canAdd) {
          availabilityEl?.remove();
        } else if (availabilityEl) {
          availabilityEl.textContent = this.labels.soldOut;
        } else {
          metaEl.insertAdjacentHTML('beforeend', `<p class="quick-add-product__availability">${this.escapeHtml(this.labels.soldOut)}</p>`);
        }
      }

      const variantInput = productRoot.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = this.currentVariant ? String(this.currentVariant.id) : '';
      }

      const submitBtn = productRoot.querySelector('.quick-add-product__submit');
      if (submitBtn) {
        submitBtn.disabled = !canAdd;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.querySelectorAll('.btn__label-text').forEach((labelEl) => {
          labelEl.textContent = submitLabel;
        });
      }

      this.slideMediaToCurrentVariant();
      this.setStatusText('');
    }

    getCurrentPriceMarkup() {
      if (!this.currentVariant) {
        // No variant — show unavailable text in muted price slot
        return `<div class="card-price"><span class="card-price__main quick-add-product__price-unavailable">${this.escapeHtml(this.labels.unavailable)}</span></div>`;
      }

      const price    = this.formatMoney(this.currentVariant.price);
      const compareAt = this.currentVariant.compare_at_price;
      const hasCompare = Number.isFinite(compareAt) && compareAt > this.currentVariant.price;

      // Note: formatMoney output intentionally inserted as raw HTML because
      // shop.money_format may contain <span class=money> wrapper tags.
      // The money format is admin-controlled trusted content, not user input.
      return `<div class="card-price"><span class="card-price__main" style="font-size: var(--text-large);">${price}</span>${hasCompare ? `<span class="card-price__compare" style="font-size: var(--text-small);">${this.formatMoney(compareAt)}</span>` : ''}</div>`;
    }

    renderOptionsMarkup() {
      if (!Array.isArray(this.productData.options) || !this.productData.options.length) return '';

      // Products without real variants expose a single "Title" option whose only
      // value is "Default Title". Skip the picker so quick view doesn't show a
      // meaningless "Title: Default Title" selector.
      const firstOption = this.productData.options[0];
      const firstOptionName = (typeof firstOption === 'string' ? firstOption : (firstOption && firstOption.name) || '').trim().toLowerCase();
      const firstOptionValues = this.getOptionValues(0);
      if (
        this.productData.has_only_default_variant === true ||
        (this.productData.options.length === 1 &&
          firstOptionName === 'title' &&
          firstOptionValues.length === 1 &&
          String(firstOptionValues[0] || '').trim().toLowerCase() === 'default title')
      ) {
        return '';
      }

      return this.productData.options
        .map((optionRaw, optionIndex) => {
          // The Shopify AJAX API may return options as strings (old /products/*.js)
          // or as objects {name, values, …} (newer API surface). Extract the name safely.
          const optionName = typeof optionRaw === 'string'
            ? optionRaw
            : (String(optionRaw?.name ?? '')); // handles {name:"Color",…} objects

          const isColorOption = this.isSwatchOption(optionName);
          const selectedValue = this.selectedOptions[optionIndex] || '';
          const groupName = `quick-add-option-${optionIndex}`;
          const valuesMarkup = this.getOptionValues(optionIndex)
            .map((value, valueIndex) => {
              const isActive = this.selectedOptions[optionIndex] === value;
              const valueState = this.getValueState(optionIndex, value);
              const isUnavailable = valueState === 'unavailable';
              const isSoldOut = valueState === 'sold-out';
              const unavailableClass = isSoldOut
                ? 'is-unavailable--strikethrough'
                : (isUnavailable ? 'is-unavailable' : '');
              const inputId = `QuickAddOption-${optionIndex}-${valueIndex}`;
              const optionLabel = `${this.escapeHtml(value)}${isSoldOut ? ` (${this.escapeHtml(this.labels.soldOut)})` : ''}`;

              if (isColorOption) {
                const swatchBackground = this.resolveSwatchBackgroundByOption(optionName, value);
                return `
                  <input
                    type="radio"
                    id="${inputId}"
                    name="${groupName}"
                    value="${this.escapeHtml(value)}"
                    class="${unavailableClass}"
                    data-qa-option-index="${optionIndex}"
                    data-qa-option-value="${this.escapeHtml(value)}"
                    data-value-state="${valueState}"
                    ${isActive ? 'checked' : ''}
                    ${isUnavailable ? 'disabled' : ''}
                  >
                  <label
                    for="${inputId}"
                    class="variant-picker__swatch-label${isSoldOut ? ' is-sold-out' : ''}"
                    data-qa-option-index="${optionIndex}"
                    data-qa-option-value="${this.escapeHtml(value)}"
                    data-value-state="${valueState}"
                    title="${this.escapeHtml(value)}"
                    aria-label="${optionLabel}"
                  >
                    <span class="variant-picker__swatch-color" style="--swatch-background:${this.escapeHtml(swatchBackground)};"></span>
                    <span class="visually-hidden">${optionLabel}</span>
                  </label>
                `;
              }

              return `
                <input
                  type="radio"
                  id="${inputId}"
                  name="${groupName}"
                  value="${this.escapeHtml(value)}"
                  class="${unavailableClass}"
                  data-qa-option-index="${optionIndex}"
                  data-qa-option-value="${this.escapeHtml(value)}"
                  data-value-state="${valueState}"
                  ${isActive ? 'checked' : ''}
                  ${isUnavailable ? 'disabled' : ''}
                >
                <label
                  for="${inputId}"
                  data-qa-option-index="${optionIndex}"
                  data-qa-option-value="${this.escapeHtml(value)}"
                  data-value-state="${valueState}"
                  aria-label="${optionLabel}"
                >
                  ${this.escapeHtml(value)}
                </label>
              `;
            })
            .join('');

          // Reuse PDP variant-picker classes so pills/swatches match product page.
          return `
            <fieldset class="quick-add-product__option variant-picker__option">
              <legend class="variant-picker__label">
                <span>${this.escapeHtml(optionName)}</span>
                ${selectedValue ? `<span class="variant-picker__label-value">${this.escapeHtml(selectedValue)}</span>` : ''}
              </legend>
              <div class="${isColorOption ? 'variant-picker__swatches variant-picker__swatches--circle' : 'variant-picker__pills'} quick-add-product__values" role="radiogroup" aria-label="${this.escapeHtml(optionName)}">
                ${valuesMarkup}
              </div>
            </fieldset>
          `;
        })
        .join('');
    }

    renderProduct() {
      if (!this.productData) {
        this.renderError(this.labels.error);
        return;
      }

      this.destroyMediaSwiper();
      this.mediaSwiperRenderToken += 1;

      const currentQuantityField = this.content.querySelector(QTY_INPUT_SELECTOR);
      const currentQuantity = Math.max(1, Number.parseInt(currentQuantityField?.value || '1', 10) || 1);

      const productTitle = this.productData.title || '';
      const productUrl = this.getCurrentProductUrl();
      const canAdd = Boolean(this.currentVariant && this.currentVariant.available);
      const soldOutMessage = !canAdd
        ? `<p class="quick-add-product__availability">${this.escapeHtml(this.labels.soldOut)}</p>`
        : '';

      const galleryImages = this.collectGalleryImages(productTitle);
      const activeMediaIndex = this.getActiveMediaIndex(galleryImages);
      const imageMarkup = this.renderMediaMarkup(galleryImages, activeMediaIndex, productTitle);
      const vendorRaw = String(this.productData.vendor || '').trim();
      const vendorLinkEnabled = document.body?.dataset?.enableProductVendorLink === 'true';
      const vendorUrl = vendorRaw ? `${this.routesRoot}collections/vendors?q=${encodeURIComponent(vendorRaw)}` : '';
      const vendorMarkup = vendorRaw
        ? (
          vendorLinkEnabled
            ? `<p class="card__vendor quick-add-product__vendor"><a class="card__vendor-link" href="${this.escapeHtml(vendorUrl)}">${this.escapeHtml(vendorRaw)}</a></p>`
            : `<p class="card__vendor quick-add-product__vendor">${this.escapeHtml(vendorRaw)}</p>`
        )
        : '';

      const submitLabel = canAdd ? this.labels.addToCart : this.labels.soldOut;
      const optionsMarkup = this.renderOptionsMarkup();

      this.content.innerHTML = `
        <article class="quick-add-product" data-quick-add-product>
          <div class="quick-add-product__summary">
            <a class="quick-add-product__media" href="${this.escapeHtml(productUrl)}">${imageMarkup}</a>
            <div class="quick-add-product__meta">
              ${vendorMarkup}
              ${this.getCurrentPriceMarkup()}
              ${soldOutMessage}
            </div>
          </div>
          ${optionsMarkup ? `<div class="quick-add-product__options">${optionsMarkup}</div>` : ''}
          <div class="quick-add-product__footer">
            <form class="quick-add-product__footer_action" data-quick-add-form>
              <div class="quantity-selector__wrap quantity-selector__wrap--compact">
                <button type="button" class="quantity-selector__btn" data-qa-qty-decrease aria-label="${this.escapeHtml(this.labels.decrease)}">−</button>
                <input id="QuickAddQty" class="quantity-selector__input" type="number" min="1" step="1" value="${currentQuantity}" inputmode="numeric" data-qa-qty-input>
                <button type="button" class="quantity-selector__btn" data-qa-qty-increase aria-label="${this.escapeHtml(this.labels.increase)}">+</button>
              </div>
              <input type="hidden" name="id" value="${this.currentVariant ? this.currentVariant.id : ''}">
              <button class="btn btn--primary btn--full btn--label-slide quick-add-product__submit" type="submit" ${canAdd ? '' : 'disabled'}>
                <span class="btn__label btn__label--slide">
                  <span class="btn__label-text btn__label-text--main">${this.escapeHtml(submitLabel)}</span>
                  <span class="btn__label-text btn__label-text--alt" aria-hidden="true">${this.escapeHtml(submitLabel)}</span>
                </span>
              </button>
            </form>
            <div class="quick-add-product__error" data-quick-add-error role="alert" hidden></div>
            <a class="btn btn--secondary btn--full btn--label-slide quick-add-product__submit" href="${this.escapeHtml(productUrl)}">
              <span class="btn__label btn__label--slide">
                <span class="btn__label-text btn__label-text--main">${this.escapeHtml(this.labels.viewDetails)}</span>
                <span class="btn__label-text btn__label-text--alt" aria-hidden="true">${this.escapeHtml(this.labels.viewDetails)}</span>
              </span>
              <span class="btn__icon" aria-hidden="true">
                <svg class="icon-icon-arrow-right" style="--icon-size: 18px;width: 18px;height: 18px;" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.5 5.5l6.5 6.5-6.5 6.5M4 12h16" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </span>
            </a>
          </div>
        </article>
      `;

      this.initMediaSwiper();
      this.setStatusText('');
    }

    setQuantity(input, value) {
      const next = Math.max(1, Number.parseInt(value, 10) || 1);
      input.value = String(next);
    }

    async submitQuickAdd(form) {
      this.setErrorText('');
      const variantId = this.currentVariant?.id;
      if (!variantId) {
        this.setErrorText(this.labels.unavailable);
        return;
      }

      // Subscription-only products (requires_selling_plan) can't be added without
      // a selling plan, and the quick-add has no plan selector. Surface the
      // constraint immediately (like the PDP / Dawn) rather than firing a doomed
      // request; the shopper can use "View details" to pick a plan.
      if (this.productData?.requires_selling_plan) {
        this.setErrorText(this.labels.sellingPlanRequired);
        return;
      }

      const quantityInput = form.querySelector(QTY_INPUT_SELECTOR);
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
      const submitButton = form.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
      }

      try {
        const payload = new FormData();
        payload.set('id', String(variantId));
        payload.set('quantity', String(quantity));

        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: payload
        });

        const json = await response.json();
        if (!response.ok) {
          throw new Error(json?.description || this.labels.error);
        }

        const onCartPage = document.body.dataset.pageType === 'cart';
        if (this.cartType === 'page' || onCartPage) {
          window.location.href = this.cartUrl;
          return;
        }

        this.close(false);
        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        document.dispatchEvent(new CustomEvent('theme:cart:open', {
          detail: { opener: this.lastTrigger || document.querySelector('[data-cart-trigger]') }
        }));
      } catch (error) {
        this.setErrorText(error?.message || this.labels.error);
      } finally {
        if (submitButton) {
          submitButton.disabled = !this.currentVariant?.available;
          submitButton.removeAttribute('aria-busy');
        }
      }
    }

    setStatusText(message) {
      if (!this.statusRegion) return;
      this.statusRegion.textContent = message || '';
    }

    // Visible error (in addition to the aria-live status region) so inventory /
    // selling-plan rejections on quick-add aren't silently swallowed. Prefer the
    // error slot rendered inside the footer (right under Add to cart, like the
    // PDP / Dawn); fall back to the static drawer-level slot before content
    // exists.
    setErrorText(message) {
      this.setStatusText(message);
      const region = this.content?.querySelector('[data-quick-add-error]') || this.errorRegion;
      if (!region) return;
      region.textContent = message || '';
      if (message) {
        region.removeAttribute('hidden');
      } else {
        region.setAttribute('hidden', '');
      }
    }

    escapeHtml(input) {
      const text = String(input ?? '');
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  if (!customElements.get('quick-add-drawer')) {
    customElements.define('quick-add-drawer', QuickAddDrawer);
  }
})();



/* --- Migrated from assets/cart.js --- */

/*
  Theme cart system (informed by premium-theme analysis):

  - Prestige/Impact:
    - Add/change via cart AJAX endpoints.
    - Bundle section ids in requests and replace cart drawer markup from returned section HTML.
    - Drawer behaves like dialog: ESC, outside close, aria-expanded sync, focus restore.

  - Symmetry:
    - Strong event delegation for remove/quantity actions.
    - Cart content merged/refreshed from section HTML rather than hand-patching rows.

  - Wonder:
    - Reuses cart item rendering between page and drawer.
    - Uses section rendering for cart drawer + cart page sections and refreshes header cart bubble.

  Adopted for Theme:
  - Shared line-item snippet for page + drawer.
  - Delegated cart interactions.
  - cart/add.js, cart/change.js, cart/update.js.
  - Section rendering refresh for `cart-drawer` and `main-cart` when present.
  - Dialog-grade drawer accessibility with focus trap + scroll lock.
*/
(() => {
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const root = window.Shopify?.routes?.root || '/';

  const routes = {
    add: `${root}cart/add.js`,
    change: `${root}cart/change.js`,
    update: `${root}cart/update.js`,
    cart: `${root}cart`
  };

  const debounce = (fn, wait) => {
    let timeoutId = null;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), wait);
    };
  };

  const getScrollbarCompensation = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  class ThemeCartController {
    constructor() {
      this.cartType = document.body.dataset.cartType || 'drawer';
      this.pageType = document.body.dataset.pageType || '';
      this.cartLabel = document.body.dataset.cartLabel || 'Cart';
      this.addErrorMessage = document.body.dataset.cartErrorAdd || 'Unable to add item to cart.';
      this.updateErrorMessage = document.body.dataset.cartErrorUpdate || 'Unable to update cart.';
      this.addedMessage = document.body.dataset.cartMessageAdded || 'Item added to cart.';
      this.updatedMessage = document.body.dataset.cartMessageUpdated || 'Cart updated.';
      this.removedMessage = document.body.dataset.cartMessageRemoved || 'Item removed from cart.';
      this.lockClass = 'theme-cart-lock';
      this.isDrawerOpen = false;
      this.drawerOpener = null;
      this.railRevealDelay = 250;
      this.railRevealTimer = null;
      this.closeAnimTimer = null;
      this.openStartRafId = 0;
      this.openCommitRafId = 0;
      this.closeAnimationMs = 360;
      this.recommendationsRoot = null;
      this.recommendationsList = null;
      this.recommendationsFallbackMarkup = '';
      this.recommendationsCache = new Map();
      this.recommendationsRequestToken = 0;
      this.lineUpdateDelay = 450;
      this.pendingLineUpdateTimers = new Map();
      this.pendingLineQuantities = new Map();

      this.persistNoteDebounced = debounce(this.persistNote.bind(this), 400);

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onOpenEvent = this.onOpenEvent.bind(this);
      this.onCloseEvent = this.onCloseEvent.bind(this);
      this.onRefreshEvent = this.onRefreshEvent.bind(this);
    }

    init() {
      this.refreshElements();
      this.syncTriggerState(false);
      this.syncCartCountFromDOM();
      this.hydrateRecommendations();

      document.addEventListener('click', this.onClick);
      document.addEventListener('submit', this.onSubmit);
      document.addEventListener('change', this.onChange);
      document.addEventListener('input', this.onInput);
      document.addEventListener('keydown', this.onKeyDown);

      document.addEventListener('theme:cart:open', this.onOpenEvent);
      document.addEventListener('theme:cart:close', this.onCloseEvent);
      document.addEventListener('theme:cart:refresh', this.onRefreshEvent);
    }

    refreshElements() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.drawerPanel = this.drawer?.querySelector('[data-cart-drawer-panel]');
      this.recommendationsRoot = this.drawer?.querySelector('[data-cart-recommendations]') || null;
      this.recommendationsList = this.drawer?.querySelector('[data-cart-recommendations-list]') || null;
      this.recommendationsFallbackMarkup = this.recommendationsList ? this.recommendationsList.innerHTML : '';
      this.syncRecommendationsRailVisibility(this.hasRecommendationMarkup(this.recommendationsFallbackMarkup));
      this.liveRegions = Array.from(document.querySelectorAll('[data-cart-live-region]'));
      this.closeAnimationMs = this.readDurationMs(this.drawer || document.documentElement, '--motion-duration-normal') || 320;
    }

    get isCartPage() {
      if (this.pageType === 'cart') return true;

      const normalizedPath = window.location.pathname.replace(/\/+$/, '');
      const cartPath = routes.cart.replace(window.location.origin, '').replace(/\/+$/, '');

      return normalizedPath === cartPath;
    }

    getSectionIds() {
      const sectionIds = new Set();

      const drawerSectionId = this.getSectionIdFromElement(this.drawer);
      if (drawerSectionId) {
        sectionIds.add(drawerSectionId);
      } else if (document.getElementById('shopify-section-cart-drawer')) {
        sectionIds.add('cart-drawer');
      }

      const cartPageSection = document.querySelector('[data-cart-page-section]');
      const cartPageSectionId = this.getSectionIdFromElement(cartPageSection);
      if (cartPageSectionId) {
        sectionIds.add(cartPageSectionId);
      }

      return Array.from(sectionIds);
    }

    getSectionIdFromElement(element) {
      if (!(element instanceof Element)) return null;

      const explicitId = element.getAttribute('data-section-id') || element.dataset.sectionId;
      if (explicitId) return explicitId;

      const hostSection = element.closest('[id^="shopify-section-"]');
      if (!hostSection) return null;

      return hostSection.id.replace('shopify-section-', '');
    }

    onOpenEvent(event) {
      this.openDrawer(event.detail?.opener || null, true);
    }

    onCloseEvent() {
      this.closeDrawer(true);
    }

    onRefreshEvent() {
      this.refreshSections();
    }

    emitCartDrawerState(open) {
      document.dispatchEvent(
        new CustomEvent('theme:cart-drawer:state', {
          detail: { open: Boolean(open) },
        })
      );
    }

    onClick(event) {
      const cartTrigger = event.target.closest('[data-cart-trigger]');
      if (cartTrigger) {
        this.handleCartTrigger(event, cartTrigger);
        return;
      }

      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }

      if (event.target.closest('[data-cart-drawer-overlay]')) {
        this.closeDrawer(true);
        return;
      }

      const quantityButton = event.target.closest('[data-cart-qty-change]');
      if (quantityButton) {
        event.preventDefault();
        this.handleQuantityButton(quantityButton);
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        const line = Number.parseInt(removeButton.dataset.cartLine, 10);
        if (Number.isFinite(line)) {
          this.cancelPendingLineUpdate(line);
          this.updateLine(line, 0);
        }
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!this.isCartAddForm(form)) return;

      const eventSubmitter = event.submitter;
      if (this.isDirectCheckoutSubmitter(eventSubmitter)) return;
      const submitter = this.resolveAddToCartSubmitter(form, eventSubmitter);

      event.preventDefault();
      this.addToCart(form, submitter);
    }

    onChange(event) {
      const quantityInput = event.target.closest('[data-cart-qty-input]');
      if (quantityInput) {
        const line = Number.parseInt(quantityInput.dataset.cartLine, 10);
        if (!Number.isFinite(line)) return;

        const normalized = this.normalizeQuantity(quantityInput.value, quantityInput);
        this.syncLineQuantityInputs(line, normalized, quantityInput);
        this.scheduleLineUpdate(line, normalized);
        return;
      }

      const noteInput = event.target.closest('[data-cart-note-input]');
      if (noteInput) {
        this.syncNoteInputs(noteInput);
        this.persistNoteDebounced(noteInput.value);
      }
    }

    onInput(event) {
      const noteInput = event.target.closest('[data-cart-note-input]');
      if (!noteInput) return;

      this.syncNoteInputs(noteInput);
      this.persistNoteDebounced(noteInput.value);
    }

    onKeyDown(event) {
      if (!this.isDrawerOpen || !this.drawerPanel) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = this.getFocusableElements(this.drawerPanel);
      if (!focusables.length) {
        event.preventDefault();
        this.focusElement(this.drawerPanel);
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!this.drawerPanel.contains(active)) {
        event.preventDefault();
        this.focusElement(first);
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        this.focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        this.focusElement(first);
      }
    }

    handleCartTrigger(event, trigger) {
      const behavior = trigger.dataset.cartBehavior || 'page';
      const canUseDrawer = behavior === 'drawer' && this.cartType === 'drawer' && !this.isCartPage && this.drawer;

      if (!canUseDrawer) return;

      event.preventDefault();
      this.openDrawer(trigger, true);
    }

    handleQuantityButton(button) {
      const line = Number.parseInt(button.dataset.cartLine, 10);
      if (!Number.isFinite(line)) return;

      const item = button.closest('[data-cart-item]');
      const quantityInput = item?.querySelector('[data-cart-qty-input]');
      if (!quantityInput) return;

      const current = Number.parseInt(quantityInput.value, 10) || 0;
      const step = Number.parseInt(quantityInput.getAttribute('step') || '1', 10) || 1;
      const min = Number.parseInt(quantityInput.getAttribute('min') || '0', 10) || 0;
      const maxAttr = quantityInput.getAttribute('max');
      const max = maxAttr ? Number.parseInt(maxAttr, 10) : null;

      let nextQuantity = current;

      if (button.dataset.cartQtyChange === 'decrease') {
        nextQuantity = Math.max(min, current - step);
      } else {
        nextQuantity = current + step;
      }

      if (max !== null && nextQuantity > max) {
        nextQuantity = max;
      }

      this.syncLineQuantityInputs(line, nextQuantity, quantityInput);
      this.scheduleLineUpdate(line, nextQuantity);
    }

    scheduleLineUpdate(line, quantity) {
      if (!Number.isFinite(line)) return;
      const targetQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);

      this.pendingLineQuantities.set(line, targetQuantity);

      const existingTimer = this.pendingLineUpdateTimers.get(line);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      const timerId = window.setTimeout(() => {
        this.pendingLineUpdateTimers.delete(line);
        const queuedQuantity = this.pendingLineQuantities.get(line);
        this.pendingLineQuantities.delete(line);

        if (!Number.isFinite(queuedQuantity)) return;
        this.updateLine(line, queuedQuantity);
      }, this.lineUpdateDelay);

      this.pendingLineUpdateTimers.set(line, timerId);
    }

    cancelPendingLineUpdate(line) {
      const timerId = this.pendingLineUpdateTimers.get(line);
      if (timerId) {
        window.clearTimeout(timerId);
      }

      this.pendingLineUpdateTimers.delete(line);
      this.pendingLineQuantities.delete(line);
    }

    syncLineQuantityInputs(line, quantity, sourceInput = null) {
      const selector = `[data-cart-qty-input][data-cart-line="${line}"]`;
      document.querySelectorAll(selector).forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        if (sourceInput && input === sourceInput) {
          input.value = quantity;
          return;
        }

        const normalized = this.normalizeQuantity(quantity, input);
        input.value = normalized;
      });
    }

    async addToCart(form, submitter) {
      const activeSubmitter = this.resolveAddToCartSubmitter(form, submitter);
      const errorTarget = form.querySelector('[data-cart-error]');
      this.clearError(errorTarget);
      form.querySelectorAll('[data-selling-plan-error]').forEach((el) => this.clearError(el));

      // Subscription-only products must be added with a selling plan.
      const sellingPlanError = this.validateSellingPlan(form);
      if (sellingPlanError) {
        this.showError(sellingPlanError.target || errorTarget, sellingPlanError.message);
        return;
      }

      this.setSubmitterLoading(activeSubmitter, true);

      try {
        const formData = new FormData(form);
        const sectionIds = this.getSectionIds();

        if (sectionIds.length) {
          formData.set('sections', sectionIds.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        const response = await fetch(routes.add, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const responseJson = await response.json();

        if (!response.ok) {
          this.showError(errorTarget, responseJson.description || this.addErrorMessage);
          return;
        }

        if (this.cartType === 'page' || this.isCartPage) {
          window.location.href = routes.cart;
          return;
        }

        if (responseJson.sections && Object.keys(responseJson.sections).length) {
          this.applySectionPayload(responseJson.sections);
        } else {
          await this.refreshSections();
        }

        this.syncCartCountFromDOM();
        this.announce(this.addedMessage);
        this.openDrawer(activeSubmitter || form.querySelector('[type="submit"]'), true);
      } catch (error) {
        console.error('Theme cart add error:', error);
        this.showError(errorTarget, this.addErrorMessage);
      } finally {
        this.setSubmitterLoading(activeSubmitter, false);
      }
    }

    async updateLine(line, quantity) {
      const targetQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
      if (!Number.isFinite(line)) return;

      this.setLineLoading(line, true);

      try {
        const payload = {
          line,
          quantity: targetQuantity
        };

        const sectionIds = this.getSectionIds();
        if (sectionIds.length) {
          payload.sections = sectionIds.join(',');
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(routes.change, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const responseJson = await response.json();

        if (!response.ok) {
          await this.refreshSections();
          this.getLineErrorTargets(line).forEach((target) =>
            this.showError(target, responseJson.description || this.updateErrorMessage)
          );
          return;
        }

        // Shopify clamps an over-stock quantity to the max available and still
        // returns 200 OK — surface that instead of silently changing the value.
        const changedItem = Array.isArray(responseJson.items) ? responseJson.items[line - 1] : null;
        const clampedQuantity =
          changedItem && typeof changedItem.quantity === 'number' ? changedItem.quantity : null;
        const wasClamped =
          clampedQuantity !== null && targetQuantity > 0 && clampedQuantity < targetQuantity;

        if (responseJson.sections && Object.keys(responseJson.sections).length) {
          this.applySectionPayload(responseJson.sections);
        } else {
          await this.refreshSections();
        }

        if (typeof responseJson.item_count === 'number') {
          this.syncCartCount(responseJson.item_count);
        } else {
          this.syncCartCountFromDOM();
        }

        if (wasClamped) {
          // Section re-render above resets the (now-hidden) error slots, so set
          // the clamp message on the freshly rendered line.
          this.getLineErrorTargets(line).forEach((target) =>
            this.showError(target, this.quantityErrorMessage(clampedQuantity))
          );
        } else {
          this.announce(targetQuantity === 0 ? this.removedMessage : this.updatedMessage);
        }
      } catch (error) {
        console.error('Theme cart change error:', error);
        await this.refreshSections();
        this.getLineErrorTargets(line).forEach((target) =>
          this.showError(target, this.updateErrorMessage)
        );
      } finally {
        this.setLineLoading(line, false);
      }
    }

    async persistNote(noteValue) {
      try {
        await fetch(routes.update, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ note: noteValue }),
          keepalive: true
        });
      } catch (error) {
        console.error('Theme cart note error:', error);
      }
    }

    async refreshSections() {
      const sectionIds = this.getSectionIds();
      if (!sectionIds.length) return;

      try {
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('sections', sectionIds.join(','));

        // NB: do not send `Accept: application/json`. On the /cart route that
        // header makes Shopify return the cart drop (like /cart.js) instead of
        // the Section Rendering payload, which silently breaks cart-page
        // refreshes. The `?sections=` param already yields `{ id: html }`.
        const response = await fetch(requestUrl.toString());

        if (!response.ok) return;

        const sectionPayload = await response.json();
        this.applySectionPayload(sectionPayload);
      } catch (error) {
        console.error('Theme cart section refresh error:', error);
      }
    }

    applySectionPayload(sectionPayload) {
      const wasDrawerOpen = this.isDrawerOpen;
      const wasRailOpen = this.drawer?.classList.contains('is-rail-open') || false;

      Object.entries(sectionPayload).forEach(([sectionId, sectionMarkup]) => {
        this.replaceSection(sectionId, sectionMarkup);
      });

      this.refreshElements();
      this.syncCartCountFromDOM();
      this.hydrateRecommendations();

      if (wasDrawerOpen && this.drawer) {
        this.restoreOpenDrawerState({ restoreRailState: wasRailOpen });
      }
    }

    restoreOpenDrawerState(options = {}) {
      if (!this.drawer) return;

      const restoreRailState = options.restoreRailState === true;

      window.clearTimeout(this.closeAnimTimer);
      this.closeAnimTimer = null;
      this.cancelPendingOpenStart();

      this.isDrawerOpen = true;

      // Keep drawer open after section HTML replacement without replaying open animation.
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.remove('is-closing');
      this.drawer.classList.add('is-open');

      if (restoreRailState) {
        this.resetRailReveal();
        this.drawer.classList.add('is-rail-open');
      } else {
        this.scheduleRailReveal();
      }

      const currentLockCount = Number.parseInt(document.documentElement.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
      document.documentElement.classList.add(this.lockClass);
      document.body.classList.add(this.lockClass);
      if (currentLockCount <= 0) {
        acquireGlobalScrollLock();
      }
      this.syncTriggerState(true);
      this.emitCartDrawerState(true);
    }

    replaceSection(sectionId, sectionMarkup) {
      const currentSection = document.getElementById(`shopify-section-${sectionId}`);
      if (!currentSection || typeof sectionMarkup !== 'string' || sectionMarkup === '') return;

      const parsed = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const parsedSection = parsed.getElementById(`shopify-section-${sectionId}`);

      if (parsedSection) {
        currentSection.innerHTML = parsedSection.innerHTML;
      } else {
        currentSection.innerHTML = sectionMarkup;
      }
    }

    openDrawer(opener, focus) {
      this.setDrawerState(true, { opener, focus });
      this.hydrateRecommendations();
    }

    closeDrawer(restoreFocus) {
      this.setDrawerState(false, { restoreFocus });
    }

    setDrawerState(isOpen, options = {}) {
      this.refreshElements();

      if (!this.drawer) {
        this.isDrawerOpen = false;
        this.resetRailReveal();
        this.syncTriggerState(false);
        return;
      }

      const focus = options.focus !== false;
      const restoreFocus = options.restoreFocus !== false;
      const restoreRailState = options.restoreRailState === true;
      const isAlreadyOpen = this.isDrawerOpen && (
        this.drawer.classList.contains('is-open')
      );

      if (isOpen) {
        // Cancel any in-progress close animation before (re-)opening.
        window.clearTimeout(this.closeAnimTimer);
        this.closeAnimTimer = null;
        this.cancelPendingOpenStart();

        if (isAlreadyOpen) {
          if (restoreRailState) {
            this.resetRailReveal();
            this.drawer.classList.add('is-rail-open');
          }

          this.syncTriggerState(true);
          this.emitCartDrawerState(true);

          if (focus) {
            const target = this.drawer.querySelector('[data-cart-drawer-close]') || this.getFocusableElements(this.drawerPanel)[0] || this.drawerPanel;
            this.focusElement(target);
          }

          return;
        }

        this.drawer.classList.remove('is-open', 'is-closing', 'is-opening');

        if (options.opener instanceof HTMLElement) {
          this.drawerOpener = options.opener;
        } else if (!this.drawerOpener && document.activeElement instanceof HTMLElement) {
          this.drawerOpener = document.activeElement;
        }

        this.isDrawerOpen = true;

        // Make the drawer visible at its closed (translateX) position first.
        this.drawer.hidden = false;
        this.drawer.setAttribute('aria-hidden', 'false');
        if (restoreRailState) {
          this.resetRailReveal();
          this.drawer.classList.add('is-rail-open');
        } else {
          this.scheduleRailReveal();
        }
        document.documentElement.classList.add(this.lockClass);
        document.body.classList.add(this.lockClass);
        acquireGlobalScrollLock();
        this.syncTriggerState(true);

        this.drawer.classList.add('is-opening');
        this.emitCartDrawerState(true);
        this.openStartRafId = window.requestAnimationFrame(() => {
          this.openStartRafId = 0;
          if (!this.isDrawerOpen || !this.drawer) return;

          if (this.drawerPanel) void this.drawerPanel.offsetHeight;
          void this.drawer.offsetHeight;

          this.openCommitRafId = window.requestAnimationFrame(() => {
            this.openCommitRafId = 0;
            if (!this.isDrawerOpen || !this.drawer) return;
            this.drawer.classList.remove('is-opening', 'is-closing');
            this.drawer.classList.add('is-open');
          });
        });

        if (focus) {
          const target = this.drawer.querySelector('[data-cart-drawer-close]') || this.getFocusableElements(this.drawerPanel)[0] || this.drawerPanel;
          window.requestAnimationFrame(() => this.focusElement(target));
        }
      } else {
        this.isDrawerOpen = false;
        this.cancelPendingOpenStart();

        this.drawer.classList.add('is-closing');
        this.drawer.classList.remove('is-opening');
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');

        document.documentElement.classList.remove(this.lockClass);
        document.body.classList.remove(this.lockClass);
        releaseGlobalScrollLock();
        this.syncTriggerState(false);
        this.emitCartDrawerState(false);

        const finishClose = () => {
          if (!this.drawer) return;
          this.drawer.classList.remove('is-closing', 'is-opening');
          this.drawer.hidden = true;
          this.resetRailReveal();
        };

        window.clearTimeout(this.closeAnimTimer);
        if (this.closeAnimationMs <= 0) {
          finishClose();
        } else {
          this.closeAnimTimer = window.setTimeout(finishClose, this.closeAnimationMs);
        }

        if (restoreFocus) {
          this.restoreFocusToOpener();
        }
      }
    }

    cancelPendingOpenStart() {
      if (this.openStartRafId) {
        window.cancelAnimationFrame(this.openStartRafId);
        this.openStartRafId = 0;
      }
      if (this.openCommitRafId) {
        window.cancelAnimationFrame(this.openCommitRafId);
        this.openCommitRafId = 0;
      }
    }

    scheduleRailReveal() {
      this.resetRailReveal();
      if (!this.drawer || !this.drawer.classList.contains('theme-cart-drawer--has-rail')) return;
      const revealDelay = 0;

      if (revealDelay <= 0) {
        this.drawer.classList.add('is-rail-open');
        return;
      }

      this.railRevealTimer = window.setTimeout(() => {
        this.railRevealTimer = null;
        this.refreshElements();

        if (!this.isDrawerOpen || !this.drawer) return;
        if (!this.drawer.classList.contains('theme-cart-drawer--has-rail')) return;

        this.drawer.classList.add('is-rail-open');
      }, revealDelay);
    }

    resetRailReveal() {
      if (this.railRevealTimer) {
        window.clearTimeout(this.railRevealTimer);
        this.railRevealTimer = null;
      }

      if (!this.drawer) return;
      this.drawer.classList.remove('is-rail-open');
    }

    hydrateRecommendations() {
      if (!this.recommendationsRoot || !this.recommendationsList) return;

      const shouldFetchRecommendations = this.recommendationsRoot.dataset.cartRecommendations === 'true';
      const productId = Number.parseInt(this.recommendationsRoot.dataset.cartRecommendationsProductId || '', 10);

      if (!shouldFetchRecommendations || !Number.isFinite(productId)) {
        this.restoreFallbackRecommendations();
        return;
      }

      const limit = Number.parseInt(this.recommendationsRoot.dataset.cartRecommendationsLimit || '6', 10) || 6;
      const cacheKey = `${productId}:${limit}`;
      const cachedMarkup = this.recommendationsCache.get(cacheKey);

      if (cachedMarkup) {
        this.recommendationsList.innerHTML = cachedMarkup;
        this.syncRecommendationsRailVisibility(this.hasRecommendationMarkup(cachedMarkup));
        this.recommendationsList.removeAttribute('aria-busy');
        return;
      }

      const requestToken = this.recommendationsRequestToken + 1;
      this.recommendationsRequestToken = requestToken;
      this.recommendationsList.setAttribute('aria-busy', 'true');

      this.fetchRecommendationsMarkup(productId, limit)
        .then((markup) => {
          if (requestToken !== this.recommendationsRequestToken || !this.recommendationsList) return;

          if (markup) {
            this.recommendationsCache.set(cacheKey, markup);
            this.recommendationsList.innerHTML = markup;
            this.syncRecommendationsRailVisibility(true);
          } else {
            this.restoreFallbackRecommendations();
          }
        })
        .catch(() => {
          if (requestToken !== this.recommendationsRequestToken) return;
          this.restoreFallbackRecommendations();
        })
        .finally(() => {
          if (requestToken !== this.recommendationsRequestToken || !this.recommendationsList) return;
          this.recommendationsList.removeAttribute('aria-busy');
        });
    }

    restoreFallbackRecommendations() {
      if (!this.recommendationsList) return;
      this.recommendationsList.innerHTML = this.recommendationsFallbackMarkup || '';
      this.syncRecommendationsRailVisibility(this.hasRecommendationMarkup(this.recommendationsFallbackMarkup));
      this.recommendationsList.removeAttribute('aria-busy');
    }

    hasRecommendationMarkup(markup) {
      return String(markup || '').replace(/\s+/g, '').length > 0;
    }

    syncRecommendationsRailVisibility(hasVisibleContent) {
      if (!this.drawer || !this.recommendationsRoot) return;
      this.recommendationsRoot.hidden = !hasVisibleContent;
      this.drawer.classList.toggle('theme-cart-drawer--has-rail', hasVisibleContent);
    }

    async fetchRecommendationsMarkup(seedProductId, limit) {
      const endpoint = new URL(`${root}recommendations/products.json`, window.location.origin);
      endpoint.searchParams.set('product_id', String(seedProductId));
      endpoint.searchParams.set('intent', 'related');
      endpoint.searchParams.set('limit', String(limit));

      const response = await fetch(endpoint.toString(), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return '';

      const payload = await response.json();
      const products = Array.isArray(payload?.products) ? payload.products : [];
      const filteredProducts = products.filter((product) => Number(product?.id) !== seedProductId);
      const productsToRender = filteredProducts.slice(0, limit);

      if (!productsToRender.length) return '';
      return this.buildRecommendationCardsMarkup(productsToRender);
    }

    buildRecommendationCardsMarkup(products) {
      return products.map((product) => this.buildRecommendationCardMarkup(product)).join('');
    }

    buildRecommendationCardMarkup(product) {
      if (!product || typeof product !== 'object') return '';

      const title = this.escapeHtml(product.title || '');
      if (!title) return '';

      const quickAddLabels = this.getQuickAddLabels();
      const productUrl = this.escapeHtml(product.url || `${root}products/${product.handle || ''}`);
      const vendorRaw = String(product.vendor || '').trim();
      const vendor = this.escapeHtml(vendorRaw);
      const vendorLinkEnabled = document.body?.dataset?.enableProductVendorLink === 'true';
      const vendorUrl = vendorRaw ? `${root}collections/vendors?q=${encodeURIComponent(vendorRaw)}` : '';
      const vendorMarkup = vendor
        ? (
          vendorLinkEnabled
            ? `<p class="theme-cart-featured__vendor"><a class="theme-cart-featured__vendor-link" href="${this.escapeHtml(vendorUrl)}">${vendor}</a></p>`
            : `<p class="theme-cart-featured__vendor">${vendor}</p>`
        )
        : '';
      const imageSource = typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.url || '';
      const imageAlt = title;

      const priceMin = Number.isFinite(product.price_min) ? product.price_min : product.price;
      const priceMax = Number.isFinite(product.price_max) ? product.price_max : priceMin;
      const hasPriceRange = Number.isFinite(priceMin) && Number.isFinite(priceMax) && priceMin !== priceMax;

      const moneyValue = Number.isFinite(priceMin) ? this.formatMoney(priceMin) : '';
      const priceMarkup = hasPriceRange
        ? `<p class="theme-cart-featured__price">From ${moneyValue}</p>`
        : `<p class="theme-cart-featured__price">${moneyValue}</p>`;

      const swatchPayload = this.buildRecommendationSwatchPayload(product);
      const swatchesMarkup = swatchPayload.markup;

      const imageMarkup = imageSource
        ? `<img src="${this.escapeHtml(imageSource)}" loading="lazy" alt="${imageAlt}">`
        : `<span class="theme-cart-featured__image-placeholder" aria-hidden="true"></span>`;

      const quickAddMarkup = this.buildRecommendationQuickAddMarkup(
        product,
        quickAddLabels,
        productUrl,
        title,
        swatchPayload.mapAttribute
      );

      return `
        <article class="theme-cart-featured__item" role="listitem">
          <a class="theme-cart-featured__link" href="${productUrl}">
            <div class="theme-cart-featured__image">${imageMarkup}</div>
            <div class="theme-cart-featured__content">
              ${vendorMarkup}
              <h3 class="theme-cart-featured__title">${title}</h3>
              ${priceMarkup}
              ${swatchesMarkup}
            </div>
          </a>
          <div class="theme-cart-featured__quick-add">
            ${quickAddMarkup}
          </div>
        </article>
      `;
    }

    buildRecommendationQuickAddMarkup(product, labels, productUrl, productTitle, swatchMapAttribute = '') {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const productHandle = this.escapeHtml(String(product?.handle || ''));
      const quickAddJsonUrl = productHandle ? `${root}products/${encodeURIComponent(product.handle)}.js` : '';
      if (variants.length === 1) {
        const variant = variants[0];
        const variantId = Number.parseInt(variant?.id, 10);
        const isAvailable = variant?.available !== false;

        if (!Number.isFinite(variantId)) {
          return `
            <button type="button" class="btn btn--primary card__quick-add-btn" disabled>
              <span class="btn__label">${labels.soldOut}</span>
            </button>
          `;
        }

        if (!isAvailable) {
          return `
            <button type="button" class="btn btn--primary card__quick-add-btn" disabled>
              <span class="btn__label">${labels.soldOut}</span>
            </button>
          `;
        }

        return `
          <form action="${root}cart/add" method="post" class="card__quick-add-form">
            <input type="hidden" name="id" value="${variantId}">
            <button type="submit" class="btn btn--primary btn--label-slide card__quick-add-btn">
              <span class="btn__label btn__label--slide">
                <span class="btn__label-text btn__label-text--main">${labels.addToCart}</span>
                <span class="btn__label-text btn__label-text--alt" aria-hidden="true">${labels.addToCart}</span>
              </span>
            </button>
          </form>
        `;
      }

      return `
        <a
          class="btn btn--primary card__quick-add-btn"
          href="${productUrl}"
          data-quick-add-trigger
          data-quick-add-close-cart
          ${productHandle ? `data-quick-add-handle="${productHandle}"` : ''}
          data-product-title="${productTitle}"
          data-product-url="${productUrl}"
          data-quick-add-url="${productUrl}"
          ${swatchMapAttribute}
          ${quickAddJsonUrl ? `data-quick-add-json-url="${quickAddJsonUrl}"` : ''}
          aria-controls="QuickAddDrawer"
          aria-expanded="false"
        >
          <span class="btn__label">${labels.chooseOptions}</span>
        </a>
      `;
    }

    getSwatchConfig() {
      const quickAddDrawer = document.querySelector('[data-quick-add-drawer]');
      const triggerRaw = quickAddDrawer?.dataset?.swatchTrigger || '';
      const customRaw = quickAddDrawer?.dataset?.customSwatches || '';

      const ThemeSwatches = window.ThemeSwatches || null;
      const triggers = ThemeSwatches?.parseTriggers
        ? ThemeSwatches.parseTriggers(triggerRaw)
        : String(triggerRaw || '')
          .replace(/&lt;br\s*\/?&gt;/gi, '||')
          .replace(/<br\s*\/?>/gi, '||')
          .replace(/\r?\n/g, '||')
          .split(/\|\||,/)
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

      const customSwatches = ThemeSwatches?.parseCustomSwatches
        ? ThemeSwatches.parseCustomSwatches(customRaw)
        : new Map();

      return { triggers, customSwatches };
    }

    isSwatchOption(optionName, triggers) {
      const normalized = String(optionName || '').trim().toLowerCase();
      if (!normalized) return false;

      const ThemeSwatches = window.ThemeSwatches || null;
      if (ThemeSwatches?.isColorOption) {
        return ThemeSwatches.isColorOption(normalized, triggers);
      }

      if (Array.isArray(triggers) && triggers.includes(normalized)) return true;
      return normalized.includes('color') || normalized.includes('colour');
    }

    getRecommendationOptionValues(product, optionEntry, optionIndex) {
      if (Array.isArray(optionEntry?.values) && optionEntry.values.length) {
        return optionEntry.values;
      }

      const optionKey = `option${optionIndex + 1}`;
      const values = [];
      const seen = new Set();
      const variants = Array.isArray(product?.variants) ? product.variants : [];

      variants.forEach((variant) => {
        const value = String(variant?.[optionKey] || '').trim();
        if (!value) return;
        const key = value.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        values.push(value);
      });

      return values;
    }

    getRecommendationVariantImage(product, optionIndex, optionValue) {
      const optionKey = `option${optionIndex + 1}`;
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const targetValue = String(optionValue || '').trim();
      if (!targetValue) return '';

      const matchingVariant = variants.find((variant) => String(variant?.[optionKey] || '') === targetValue);
      if (!matchingVariant) return '';

      const featuredImage = matchingVariant.featured_image;
      if (typeof featuredImage === 'string' && featuredImage.trim()) return featuredImage.trim();
      if (featuredImage?.src) return String(featuredImage.src).trim();
      if (featuredImage?.url) return String(featuredImage.url).trim();
      if (matchingVariant?.image?.src) return String(matchingVariant.image.src).trim();
      if (matchingVariant?.image?.url) return String(matchingVariant.image.url).trim();

      return '';
    }

    buildRecommendationSwatchPayload(product) {
      const payload = {
        markup: '',
        mapAttribute: ''
      };
      if (!product || typeof product !== 'object') return payload;

      const options = Array.isArray(product.options) ? product.options : [];
      if (!options.length) return payload;

      const { triggers, customSwatches } = this.getSwatchConfig();

      let swatchOption = null;
      let swatchOptionIndex = -1;

      for (let index = 0; index < options.length; index += 1) {
        const option = options[index];
        const optionName = typeof option === 'string' ? option : option?.name;
        if (this.isSwatchOption(optionName, triggers)) {
          swatchOption = option;
          swatchOptionIndex = index;
          break;
        }
      }

      if (!swatchOption) return payload;

      const optionNameLower = String(
        typeof swatchOption === 'string' ? swatchOption : swatchOption?.name || ''
      ).trim().toLowerCase();

      const values = this.getRecommendationOptionValues(product, swatchOption, swatchOptionIndex).slice(0, 4);
      if (!values.length) return payload;

      const swatchMap = {};
      const swatches = values
        .map((entry) => {
          const label = String(entry?.value || entry || '').trim();
          if (!label) return '';
          const labelKey = label.toLowerCase();

          let swatchBackground = '';

          if (customSwatches.has(labelKey)) {
            swatchBackground = customSwatches.get(labelKey);
          }

          if (!swatchBackground && entry?.swatch?.image?.src) {
            swatchBackground = `url(${String(entry.swatch.image.src).trim()})`;
          } else if (!swatchBackground && entry?.swatch?.image?.url) {
            swatchBackground = `url(${String(entry.swatch.image.url).trim()})`;
          } else if (!swatchBackground && entry?.swatch?.color?.rgb) {
            swatchBackground = `rgb(${entry.swatch.color.rgb})`;
          }

          if (!swatchBackground) {
            const variantImage = this.getRecommendationVariantImage(product, swatchOptionIndex, label);
            if (variantImage) {
              swatchBackground = `url(${variantImage})`;
            }
          }

          if (!swatchBackground) {
            swatchBackground = this.sanitizeColorValue(label) || 'rgb(var(--color-foreground-rgb) / 0.14)';
          }

          swatchMap[labelKey] = swatchBackground;
          if (optionNameLower) {
            swatchMap[`${optionNameLower}::${labelKey}`] = swatchBackground;
          }

          return `
            <span class="theme-cart-featured__swatch" style="--swatch-bg: ${this.escapeHtml(swatchBackground)};" title="${this.escapeHtml(label)}" aria-hidden="true">
              <span class="theme-cart-featured__swatch-chip"></span>
            </span>
          `;
        })
        .filter(Boolean)
        .join('');

      if (!swatches) return payload;

      payload.markup = `<div class="theme-cart-featured__swatches" aria-hidden="true">${swatches}</div>`;

      const mapKeys = Object.keys(swatchMap);
      if (mapKeys.length) {
        payload.mapAttribute = `data-qa-swatch-map="${this.escapeHtml(JSON.stringify(swatchMap))}"`;
      }

      return payload;
    }

    sanitizeColorValue(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) return '';

      if (/^#[0-9a-fA-F]{3,8}$/.test(rawValue)) return rawValue;
      if (/^(rgb|hsl)a?\([\d\s.,%]+\)$/i.test(rawValue)) return rawValue;
      if (/^[a-zA-Z][a-zA-Z\s-]{1,30}$/.test(rawValue)) return rawValue.toLowerCase();

      return '';
    }

    getMoneyFormat() {
      return (
        window.theme?.moneyFormat ||
        window.Shopify?.money_format ||
        '${{amount}}'
      );
    }

    getQuickAddLabels() {
      const quickAddDrawer = document.querySelector('[data-quick-add-drawer]');
      const addToCartLabel = this.escapeHtml(quickAddDrawer?.dataset?.labelAddToCart || 'Add to cart');
      const soldOutLabel = this.escapeHtml(quickAddDrawer?.dataset?.labelSoldOut || 'Sold out');
      const chooseOptionsLabel = this.escapeHtml(quickAddDrawer?.dataset?.labelChooseOptions || 'Choose options');

      return {
        addToCart: addToCartLabel,
        soldOut: soldOutLabel,
        chooseOptions: chooseOptionsLabel
      };
    }

    formatMoney(cents) {
      const value = Number.parseInt(cents, 10);
      if (!Number.isFinite(value)) return '';

      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(value, this.getMoneyFormat());
      }

      const moneyFormat = this.getMoneyFormat();
      const formatWithDelimiters = (
        amount,
        precision = 2,
        thousands = ',',
        decimal = '.'
      ) => {
        if (!Number.isFinite(amount)) return '0';
        const number = (amount / 100).toFixed(precision);
        const [wholePart, decimalPart = ''] = number.split('.');
        const wholeWithDelimiters = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return precision > 0
          ? `${wholeWithDelimiters}${decimal}${decimalPart}`
          : wholeWithDelimiters;
      };

      const tokenMap = {
        amount: formatWithDelimiters(value, 2, ',', '.'),
        amount_no_decimals: formatWithDelimiters(value, 0, ',', '.'),
        amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
        amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
        amount_no_decimals_with_space_separator: formatWithDelimiters(value, 0, ' ', '.'),
        amount_with_apostrophe_separator: formatWithDelimiters(value, 2, "'", '.')
      };

      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => tokenMap[token] || tokenMap.amount);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    readDurationMs(element, customPropertyName) {
      if (!(element instanceof HTMLElement)) return 0;

      const value = getComputedStyle(element).getPropertyValue(customPropertyName).trim();
      if (!value) return 0;

      if (value.endsWith('ms')) {
        const parsedMs = Number.parseFloat(value);
        return Number.isFinite(parsedMs) ? parsedMs : 0;
      }

      if (value.endsWith('s')) {
        const parsedSeconds = Number.parseFloat(value);
        return Number.isFinite(parsedSeconds) ? parsedSeconds * 1000 : 0;
      }

      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    restoreFocusToOpener() {
      const opener = this.drawerOpener;
      if (!(opener instanceof HTMLElement)) return;
      if (opener.hasAttribute('disabled') || opener.getAttribute('aria-disabled') === 'true') return;

      let focusTarget = opener;
      if (opener.closest('[data-cart-drawer]')) {
        const fallback = document.querySelector('[data-cart-trigger][data-cart-behavior="drawer"]');
        if (fallback instanceof HTMLElement) {
          focusTarget = fallback;
        }
      }
      if (!document.contains(focusTarget)) return;

      window.requestAnimationFrame(() => {
        this.focusElement(focusTarget);
      });
    }

    focusElement(element) {
      if (!(element instanceof HTMLElement)) return;
      if (typeof element.focus !== 'function') return;

      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    }

    syncTriggerState(isOpen) {
      const drawerTriggers = document.querySelectorAll('[data-cart-trigger][data-cart-behavior="drawer"]');
      drawerTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    getFocusableElements(container) {
      if (!container) return [];

      return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.hidden) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.offsetParent === null && el !== document.activeElement) return false;
        return true;
      });
    }

    normalizeQuantity(rawValue, input) {
      const min = Number.parseInt(input.getAttribute('min') || '0', 10) || 0;
      const maxAttr = input.getAttribute('max');
      const max = maxAttr ? Number.parseInt(maxAttr, 10) : null;
      const step = Number.parseInt(input.getAttribute('step') || '1', 10) || 1;

      let value = Number.parseInt(rawValue, 10);
      if (!Number.isFinite(value)) value = min || 1;
      if (value < min) value = min;
      if (max !== null && value > max) value = max;

      const stepsFromMin = Math.round((value - min) / step);
      let normalized = min + (stepsFromMin * step);

      if (normalized < min) normalized = min;
      if (max !== null && normalized > max) normalized = max;

      return normalized;
    }

    setLineLoading(line, isLoading) {
      const selector = `[data-cart-item][data-cart-line="${line}"]`;
      document.querySelectorAll(selector).forEach((item) => {
        item.classList.toggle('is-updating', isLoading);
        item.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        item.querySelectorAll('[data-cart-qty-change], [data-cart-remove]').forEach((control) => {
          if (control instanceof HTMLButtonElement) {
            control.disabled = isLoading;
            control.setAttribute('aria-busy', isLoading ? 'true' : 'false');
          }
        });

        item.querySelectorAll('[data-cart-qty-input]').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.disabled = isLoading;
            input.setAttribute('aria-busy', isLoading ? 'true' : 'false');
          }
        });
      });
    }

    setSubmitterLoading(submitter, isLoading) {
      if (!(submitter instanceof HTMLElement)) return;

      if ('disabled' in submitter) {
        submitter.disabled = isLoading;
      }

      submitter.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    resolveAddToCartSubmitter(form, submitter) {
      if (!(form instanceof HTMLFormElement)) return null;

      if (submitter instanceof HTMLElement && !this.isDirectCheckoutSubmitter(submitter)) {
        return submitter;
      }

      const selectors = [
        '[data-atc-btn]',
        'button[type="submit"][name="add"]',
        'button[type="submit"]:not([name="checkout"])',
        'input[type="submit"][name="add"]',
        'input[type="submit"]:not([name="checkout"])'
      ];

      for (const selector of selectors) {
        const candidate = form.querySelector(selector);
        if (!(candidate instanceof HTMLElement)) continue;
        if (this.isDirectCheckoutSubmitter(candidate)) continue;
        if (candidate.closest('.shopify-payment-button')) continue;
        return candidate;
      }

      return null;
    }

    syncNoteInputs(sourceInput) {
      document.querySelectorAll('[data-cart-note-input]').forEach((noteInput) => {
        if (noteInput === sourceInput) return;
        noteInput.value = sourceInput.value;
      });
    }

    syncCartCountFromDOM() {
      const countNode = document.querySelector('[data-cart-count-value]');
      if (!countNode) return;

      const count = Number.parseInt(countNode.textContent, 10);
      if (!Number.isFinite(count)) return;

      this.syncCartCount(count);
    }

    syncCartCount(count) {
      const normalizedCount = Number.isFinite(count) ? Math.max(0, count) : 0;
      const hasItems = normalizedCount > 0;
      const countText = String(normalizedCount);
      const countDisplay = normalizedCount > 99 ? '99+' : countText;

      document.querySelectorAll('[data-cart-count]').forEach((countNode) => {
        const previousCount = Number.parseInt(countNode.dataset.cartCount || countNode.textContent, 10);
        const hasPreviousCount = Number.isFinite(previousCount);

        countNode.textContent = countDisplay;
        countNode.dataset.cartCount = countText;
        countNode.hidden = !hasItems;

        if (!hasItems || !hasPreviousCount || previousCount === normalizedCount) return;

        countNode.classList.remove('count-animate');
        void countNode.offsetWidth;
        countNode.classList.add('count-animate');
      });

      document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
        const previousCount = Number.parseInt(trigger.dataset.cartItemCount || '', 10);
        const hasPreviousCount = Number.isFinite(previousCount);

        trigger.dataset.cartItemCount = countText;
        trigger.classList.toggle('has-items', hasItems);

        if (hasPreviousCount && previousCount !== normalizedCount) {
          trigger.classList.remove('bag-animate');
          void trigger.offsetWidth;
          trigger.classList.add('bag-animate');
        }

        const label = trigger.dataset.cartLabel || this.cartLabel;
        trigger.setAttribute('aria-label', hasItems ? `${label} (${normalizedCount})` : label);
      });
    }

    announce(message) {
      if (!message) return;
      this.liveRegions.forEach((region) => {
        region.textContent = message;
      });
    }

    /**
     * Show a visible error and announce it to assistive tech.
     * @param {Element|null} target - the [data-cart-error] / [data-line-error] slot
     * @param {string} message
     */
    showError(target, message) {
      this.announce(message);
      if (!(target instanceof Element)) return;
      target.textContent = message || '';
      if (message) {
        target.removeAttribute('hidden');
      } else {
        target.setAttribute('hidden', '');
      }
    }

    clearError(target) {
      if (!(target instanceof Element)) return;
      target.textContent = '';
      target.setAttribute('hidden', '');
    }

    /**
     * Subscription-only products (requires_selling_plan) can't be added without
     * a selling plan. Returns {message, target} when the constraint is violated,
     * otherwise null. Mirrors Dawn's on-submit selling-plan validation.
     */
    validateSellingPlan(form) {
      const group = form.querySelector('[data-requires-selling-plan="true"]');
      if (!group) return null;

      const selected = form.querySelector(
        'input[name="selling_plan"]:checked, select[name="selling_plan"]'
      );
      const value = selected ? selected.value : '';
      if (value) return null;

      return {
        message: group.dataset.sellingPlanErrorText || this.addErrorMessage,
        target: group.querySelector('[data-selling-plan-error]') || null,
      };
    }

    /** All visible per-line error slots for a given cart line (drawer + page). */
    getLineErrorTargets(line) {
      return Array.from(
        document.querySelectorAll(`[data-cart-item][data-cart-line="${line}"] [data-line-error]`)
      );
    }

    /**
     * Build the "you can only add N" message from the body data attribute.
     * @param {number} available
     */
    quantityErrorMessage(available) {
      const template =
        document.body.dataset.cartErrorQuantity || 'You can only add [quantity] of this item to your cart.';
      return template.replace('[quantity]', String(available));
    }

    isCartAddForm(form) {
      if (!form.action) return false;

      const actionPath = new URL(form.action, window.location.origin).pathname;
      return /\/cart\/add$/.test(actionPath);
    }

    isDirectCheckoutSubmitter(submitter) {
      if (!(submitter instanceof HTMLElement)) return false;
      if (submitter.getAttribute('name') === 'checkout') return true;
      if (submitter.classList.contains('shopify-payment-button__button')) return true;
      if (submitter.closest('.shopify-payment-button')) return true;

      return false;
    }
  }

  const controller = new ThemeCartController();
  controller.init();
})();
